import { z } from 'zod';
import { IncomeStreamType, Wrapper } from './domain.js';
import { PRESET_BANNED_WORDS } from './historical-presets.js';
import { importReviewSchema } from './import-review.js';
import { bpsSchema, clientIdSchema, frequencySchema, incomeStreamTypeSchema, firmAssumptionPresetSchema, firmAssumptionPresetsSchema, isoDateSchema, liabilityTypeSchema, nonNegativePenceSchema, scenarioReadSchema, spendingCategorySchema, taxRegionSchema, wrapperSchema, } from './schemas.js';
/**
 * Wire contract for the v2 HOUSEHOLD projection (packages/engine
 * `projectHousehold`). Field names mirror the engine input exactly so the API
 * route's mapping is mechanical; the engine re-validates everything again
 * (fail-closed twice). Amounts are integer pence on the wire (hard-rules #5);
 * the branded `Pence` type is applied at the domain boundary. All objects are
 * strict: unknown keys on a financial payload are rejected with a 422.
 */
const labelSchema = z.string().trim().min(1, 'A label is required').max(200);
const yearSchema = z.number().int().min(1900).max(2200);
const ageSchema = z.number().int().min(0).max(130);
const idSchema = z.string().trim().min(1).max(120);
export const personIdSchema = z.enum(['client', 'partner']);
export const assetOwnerSchema = z.enum(['client', 'partner', 'joint']);
/** First-class escalation (FEATURES §0.1): fixed % | CPI/RPI/earnings + x% |
 * a named custom index + x% | none. Every rate-bearing kind may carry a
 * `capBps`/`floorBps` collar (e.g. DB "CPI capped 5%, floor 0%"). The rate or
 * delta is bounded at ±100%/yr so compounding can never overflow safe pence. */
const escalationBps = bpsSchema.min(-10_000).max(10_000);
const collar = {
    capBps: escalationBps.optional(),
    floorBps: escalationBps.optional(),
};
const indexNameSchema = z.string().trim().min(1).max(60);
/** A simple (constant-rate) escalation — also the shape of a stepped step. */
const simpleEscalationSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('fixed'), rateBps: escalationBps, ...collar }).strict(),
    z.object({ kind: z.literal('cpiPlus'), deltaBps: escalationBps, ...collar }).strict(),
    z.object({ kind: z.literal('rpiPlus'), deltaBps: escalationBps, ...collar }).strict(),
    z.object({ kind: z.literal('earningsPlus'), deltaBps: escalationBps, ...collar }).strict(),
    z
        .object({
        kind: z.literal('indexPlus'),
        index: indexNameSchema,
        deltaBps: escalationBps,
        ...collar,
    })
        .strict(),
    z.object({ kind: z.literal('none') }).strict(),
]);
export const escalationSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('fixed'), rateBps: escalationBps, ...collar }).strict(),
    z.object({ kind: z.literal('cpiPlus'), deltaBps: escalationBps, ...collar }).strict(),
    z.object({ kind: z.literal('rpiPlus'), deltaBps: escalationBps, ...collar }).strict(),
    z.object({ kind: z.literal('earningsPlus'), deltaBps: escalationBps, ...collar }).strict(),
    z
        .object({
        kind: z.literal('indexPlus'),
        index: indexNameSchema,
        deltaBps: escalationBps,
        ...collar,
    })
        .strict(),
    z
        .object({
        kind: z.literal('stepped'),
        steps: z
            .array(z.object({ fromYear: yearSchema, rate: simpleEscalationSchema }).strict())
            .min(1)
            .max(20),
        ...collar,
    })
        .strict(),
    z.object({ kind: z.literal('none') }).strict(),
]);
export const statePensionEscalationSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('tripleLockProxy') }).strict(),
    z.object({ kind: z.literal('flat'), rateBps: bpsSchema.min(-10_000).max(10_000) }).strict(),
]);
/**
 * The student loan plans the model implements.
 *
 * Plan 4 (study funded by the Student Awards Agency for Scotland) is
 * DELIBERATELY ABSENT. It carries its own threshold and term, and substituting
 * any other plan would silently mis-deduct — so the contract refuses it at the
 * boundary rather than accepting a value nothing downstream can honour. The
 * fact-find shows Plan 4 as a VISIBLE, DISABLED option with a stated boundary:
 * an adviser who simply cannot find it picks "the closest" and is then wrong
 * with no trace.
 *
 * Plan 4 is not covered by the existing Scottish-taxpayer gate either — the
 * plan follows who funded the study, not where the client lives now, so an
 * `rUK` taxpayer can hold one.
 *
 * Plan 1 covers England/Wales pre-September-2012 starters AND all of Northern
 * Ireland, which is rest-of-UK and fully in scope. Nothing gates it.
 */
export const studentLoanPlanSchema = z.enum(['plan1', 'plan2', 'plan5', 'postgrad']);
/**
 * One student loan held by one person. Strictly individual (FEATURES §0.4) —
 * a student loan is never joint, and it is never a household liability row.
 *
 * There is NO balance field, and that is a decision, not an omission: a balance
 * with no statutory interest rate amortises far too fast, so repayments would
 * stop early, take-home would rise early, and projected wealth and the estate
 * would both be overstated. A wrong figure that flatters the client is worse
 * than an absent one. Repayments therefore end on the plan's write-off term
 * alone. See docs/features/2026-08-26-student-loans.md.
 */
export const householdStudentLoanSchema = z
    .object({
    plan: studentLoanPlanSchema,
    /**
     * The April in which the loan first fell due for repayment. This ALONE
     * anchors the write-off year (FEATURES §0.2) — "age 65 for the oldest
     * Plan 1 loans" is a consequence of when those loans fell due, not a second
     * input, so changing a date of birth must never move the write-off.
     *
     * NULLABLE = the plan is on file and the year is NOT YET STATED (FEEDBACK
     * 2026-09-09). An adviser cannot record a loan and its year in the same
     * keystroke: choosing the plan comes first, and the year is asked for
     * next. That in-between is a real state of a real fact-find, so the
     * contract has to be able to hold it.
     *
     * It used to be held as `0` instead, which `yearSchema` rejects — so from
     * the moment a plan was chosen the whole document failed validation: a red
     * "must be greater than or equal to 1900" under a field nobody had touched,
     * a row that reflowed around the error, and autosave blocked until the year
     * was typed. An out-of-range sentinel is not a way of saying "not yet"; it
     * is a way of saying "invalid", and the schema believed it.
     *
     * `null` is not a licence to model the loan anyway. The engine refuses a
     * loan with no year and warns `student-loan-year-not-stated`: an unanchored
     * write-off would either stop repayments early (overstating take-home pay,
     * wealth and the estate) or run them forever, and both are worse than a
     * stated gap.
     */
    firstRepaymentDueYear: yearSchema.nullable(),
})
    .strict();
/**
 * How a person's age is known when the note gave an AGE, not a birthday
 * (remediation spec 2026-09-17 §5, N20). An age is evidence about a date — it
 * is never turned into an invented 1 January birthday on an identity record.
 *
 *  exactAgeAsOf       — "Samuel is 45", said on `asOfDate`
 *  approximateAgeAsOf — "Tom is about 38": the stated figure, flagged; no ±
 *                       band is invented
 *  birthYear          — "born in 2015"
 *
 * `asOfDate` is the MEETING's date, fixed when the fact was recorded, so
 * re-running a plan on a later date never reinterprets what was said.
 * OPTIONAL and additive: a stated date of birth needs no evidence object.
 */
export const ageEvidenceSchema = z.discriminatedUnion('kind', [
    z
        .object({ kind: z.literal('exactAgeAsOf'), ageYears: ageSchema, asOfDate: isoDateSchema })
        .strict(),
    z
        .object({
        kind: z.literal('approximateAgeAsOf'),
        ageYears: ageSchema,
        asOfDate: isoDateSchema,
    })
        .strict(),
    z.object({ kind: z.literal('birthYear'), year: yearSchema }).strict(),
]);
export const householdPersonSchema = z
    .object({
    id: personIdSchema,
    /**
     * Date of birth, when the adviser has captured it. OPTIONAL and additive:
     * documents saved before this field existed stay valid. `currentAge` below
     * remains the engine's single age input — the builder derives it from this
     * date when one is set, so the two can never drift.
     */
    dateOfBirth: isoDateSchema.nullable().optional(),
    currentAge: ageSchema,
    retirementAge: ageSchema,
    statePensionAge: ageSchema,
    deathAge: ageSchema.nullable(),
    taxRegion: taxRegionSchema,
    /**
     * Recorded context, not a calculation input. A previous marriage is why a
     * pension sharing order, an ex-spouse still named on an expression of wish,
     * or a maintenance commitment shows up in a plan, so the fact-find records
     * it rather than leaving the adviser to infer it from a note.
     * OPTIONAL and additive.
     */
    maritalStatus: z
        .enum(['single', 'married', 'civilPartnership', 'divorced', 'widowed'])
        .optional(),
    /**
     * What this person is actually called. Ownership then reads as "Sarah
     * Smith" rather than "Partner": an adviser talking a plan through with a
     * couple should not have to translate an abstract role back to a person.
     *
     * OPTIONAL and additive — documents saved before this field existed stay
     * valid, and every display site falls back to the role label when it is
     * absent, so no stored plan needs rewriting (EX-1: no backfill).
     * Recorded context only: nothing in the projection reads it.
     */
    name: labelSchema.optional(),
    /**
     * This person's own student loans. OPTIONAL and additive: every document
     * and every stored projection snapshot saved before this field existed
     * still validates and re-runs byte-identically — absent means no loan,
     * which changes nothing.
     *
     * A LIST because an undergraduate plan and a postgraduate loan genuinely
     * run concurrently, each on its own threshold and rate. Modelling only one
     * would silently under-deduct for a very common case.
     */
    studentLoans: z.array(householdStudentLoanSchema).max(4).optional(),
    /** How `currentAge` is known when no date of birth was given. Recorded
     * context only; the engine reads `currentAge`. */
    ageEvidence: ageEvidenceSchema.optional(),
})
    .strict();
/**
 * A dependent's name is the only label they have — the fact-find used to carry
 * a separate `label` as well, which meant typing the same child's name twice.
 *
 * `label` stays accepted (now optional) so documents saved under the old shape
 * still validate; `name` is preferred wherever both are present. `dateOfBirth`
 * remains the stored truth because the engine measures school-fee and
 * university ages from it — the builder captures a birth YEAR as a view over
 * that date rather than splitting it into a second field the two could drift
 * between.
 */
export const householdDependentSchema = z
    .object({
    id: idSchema,
    label: labelSchema.optional(),
    name: labelSchema.optional(),
    /**
     * The stated date of birth. OPTIONAL from 2026-09-18 (spec §5, N20): a
     * child known only by age carries `ageEvidence` instead, and the engine
     * models them from `dependentModellingDateOfBirth`. Every document saved
     * before this change has a date here and is read unchanged — including the
     * 1 January dates earlier imports derived, whose provenance is unknown and
     * is NOT retrospectively relabelled as an estimate.
     */
    dateOfBirth: isoDateSchema.optional(),
    ageEvidence: ageEvidenceSchema.optional(),
})
    .strict()
    .refine((d) => d.name !== undefined || d.label !== undefined, {
    message: 'A name is required',
    path: ['name'],
})
    .refine((d) => d.dateOfBirth !== undefined || d.ageEvidence !== undefined, {
    message: 'A date of birth or an age is required',
    path: ['dateOfBirth'],
});
/**
 * The birth date the MODEL uses for a dependant: the stated date when there is
 * one; otherwise the date that makes the stated age exact on the day it was
 * stated (age 8 said on 2026-09-16 → modelled as born 2018-09-16), or 1 July
 * of a stated birth year. A disclosed modelling assumption, never written back
 * to the record — the record keeps what the note said.
 */
export function dependentModellingDateOfBirth(d) {
    if (d.dateOfBirth !== undefined)
        return d.dateOfBirth;
    const ev = d.ageEvidence;
    if (ev === undefined) {
        throw new Error('dependant has neither a date of birth nor an age');
    }
    if (ev.kind === 'birthYear')
        return `${String(ev.year).padStart(4, '0')}-07-01`;
    const year = Number(ev.asOfDate.slice(0, 4)) - ev.ageYears;
    const monthDay = ev.asOfDate.slice(5);
    const leap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    const safe = monthDay === '02-29' && !leap(year) ? '02-28' : monthDay;
    return `${String(year).padStart(4, '0')}-${safe}`;
}
/** Plain words for how a dependant's age is known — for the People card and reports. */
export function dependentAgeBasis(d) {
    if (d.dateOfBirth !== undefined)
        return null;
    const ev = d.ageEvidence;
    if (ev === undefined)
        return null;
    if (ev.kind === 'birthYear')
        return `Born in ${ev.year}; modelled from 1 July that year.`;
    const said = ev.kind === 'approximateAgeAsOf' ? `about ${ev.ageYears}` : `${ev.ageYears}`;
    const on = longDate(ev.asOfDate);
    return `Age ${said} on ${on}, as stated; no date of birth recorded. Modelled as if born on ${longDate(dependentModellingDateOfBirth(d))} — enter the real date to replace it.`;
}
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
/** "2026-09-16" → "16 September 2026" — no clock, no locale. */
function longDate(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return `${d} ${MONTHS[m - 1]} ${y}`;
}
/** The dependent's display name under either the new or the legacy shape. */
export function dependentName(d) {
    return d.name ?? d.label ?? '';
}
/** Fallbacks used wherever a person has not been named yet. */
const PERSON_ROLE_LABELS = {
    client: 'Client',
    partner: 'Partner',
    joint: 'Joint',
};
/**
 * How a person or an ownership share should be written on screen and in the
 * PDF. One resolver so a name cannot appear on the assets tab but not in a
 * chart legend: every ownership selector, table column, legend and report cell
 * asks this and nothing hard-codes "Client"/"Partner" again.
 *
 * `joint` deliberately names both people when both are known ("John & Sarah"),
 * because "Joint" alone tells an adviser nothing they did not already know.
 */
export function ownerDisplayName(ownerId, people) {
    if (ownerId === 'joint') {
        const named = people.map((p) => p.name).filter((n) => n !== undefined);
        return named.length === people.length && named.length > 0 ? named.join(' & ') : 'Joint';
    }
    return people.find((p) => p.id === ownerId)?.name ?? PERSON_ROLE_LABELS[ownerId] ?? ownerId;
}
export const householdIncomeStreamSchema = z
    .object({
    id: idSchema,
    label: labelSchema,
    ownerId: personIdSchema,
    type: incomeStreamTypeSchema,
    /**
     * The household asset this income comes FROM — a rental stream names the
     * let property, a dividend stream the investment account, savings interest
     * the cash account. OPTIONAL and additive: documents saved before this
     * field existed stay valid and mean "not recorded".
     *
     * Deliberately NOT cross-checked against `assets[].id`: an asset can be
     * deleted after the link is made, and a refine here would then reject the
     * whole document on the next write — locking the adviser out (the exact
     * failure mode the single-main-residence rule's READ/WRITE note warns
     * about). A dangling id everywhere means "no link": the builder clears it
     * on load and the engine ignores it.
     */
    sourceAssetId: idSchema.nullable().optional(),
    /**
     * Descriptive employment metadata the note stated (spec §9, N18) — job
     * title, working pattern, employed/self-employed, and which part of pay
     * this row is. Recorded context only: nothing in the projection reads
     * them. OPTIONAL and additive.
     */
    roleTitle: labelSchema.optional(),
    workingPattern: labelSchema.optional(),
    employmentBasis: z.enum(['employed', 'self_employed', 'unknown']).optional(),
    payComponent: z.enum(['base', 'bonus', 'commission']).optional(),
    amount: nonNegativePenceSchema,
    frequency: frequencySchema,
    startYear: yearSchema.nullable(),
    endYear: yearSchema.nullable(),
    /** §0.2b age anchors: start/stop at the owner's age (explicit year wins). */
    startAge: ageSchema.nullable().optional(),
    endAge: ageSchema.nullable().optional(),
    endsAtRetirement: z.boolean(),
    /** Escalation override, hard-capped at ±100%/yr — no silent runaway rates. */
    growthRateBps: bpsSchema
        .min(-10_000, 'Growth cannot be below -10,000 bps (-100%)')
        .max(10_000, 'Growth cannot exceed 10,000 bps (+100%)')
        .nullable(),
    taxable: z.boolean(),
    /**
     * B2.3 — this income is a distribution from a TRUST. It changes no figure:
     * the amount is taxed at the recipient's own rates exactly as any other
     * taxable stream is. What it does is let the plan say what it has NOT
     * modelled — the tax the trust has already paid, and the credit or reclaim
     * that goes with it — which for a discretionary trust makes the after-tax
     * figure here materially LOWER than reality.
     *
     * A flag rather than a new income TYPE, which is the constraint the brief
     * sets, and rather than matching on a label, which would silently miss an
     * adviser who wrote "Distribution from the family trust".
     */
    fromTrust: z.boolean().optional(),
    // W1.9 (class fix): `percentOrBlank`, so CLEARING it writes `null`.
    dbSpouseFractionBps: bpsSchema.min(0).max(10_000).nullish(),
    /**
     * Share of this entitlement transferred away by a PENSION SHARING ORDER on
     * divorce, in basis points (3,400 = 34%). The engine pays the entitlement
     * net of the debit.
     *
     * Distinct from `dbSpouseFractionBps`, which is what a surviving spouse
     * receives on death — this is a slice permanently removed from the member's
     * own pension by a court order, usually years earlier.
     */
    // W1.9 (class fix): `percentOrBlank`, so CLEARING it writes `null`.
    pensionSharingDebitBps: bpsSchema.min(0).max(10_000).nullish(),
})
    .strict()
    .refine((s) => s.fromTrust !== true || s.taxable === true, {
    /**
     * B2.3 review (both personas, independently). A stream marked as coming
     * from a trust but NOT taxable made the plan charge nothing and then print
     * a note saying "taxed here at the recipient's own rates … recording only
     * what arrived understates what they have" — a client-facing claim about a
     * treatment that was not applied, with the direction inverted. It is the
     * B2.2 defect (a note raised from the input rather than the assessment) one
     * item later, so the contract refuses the pairing rather than leaving the
     * engine to describe it.
     */
    message: 'A payment out of a trust is taxable income — untick "paid out of a trust", or mark it taxable',
    path: ['fromTrust'],
})
    .refine((s) => s.fromTrust !== true ||
    (s.type !== IncomeStreamType.Employment && s.type !== IncomeStreamType.SelfEmployment), {
    /**
     * And a distribution is never earnings. Recorded as employment the plan
     * charges National Insurance on it — measured at £10,919 on a £50,000
     * stream where the same money as `other` bears £7,925.
     */
    message: 'A payment out of a trust is not employment or self-employment income',
    path: ['type'],
});
export const propertyDetailsSchema = z
    .object({
    isBuyToLet: z.boolean(),
    isPrimaryResidence: z.boolean().optional(),
    rentalYieldBps: bpsSchema.min(0).max(2000),
    linkedLiabilityId: idSchema.nullable(),
})
    .strict()
    .refine((p) => !(p.isBuyToLet && p.isPrimaryResidence === true), {
    message: 'A property cannot be both the main home and a buy-to-let',
    path: ['isPrimaryResidence'],
});
export const householdAssetSchema = z
    .object({
    id: idSchema,
    label: labelSchema,
    ownerId: assetOwnerSchema,
    wrapper: wrapperSchema,
    currentValue: nonNegativePenceSchema,
    /**
     * W1.9 (wave-1 gate): NULLISH, not merely optional. The builder field is
     * `poundsOrBlank`, so CLEARING it writes `null` — and while the contract
     * took only `undefined`, that blanked the whole projection behind a generic
     * "N field(s) need attention" naming no field. B1.1 documents what blank
     * MEANS on a business ("the whole proceeds are treated as gain"), so blank
     * has to be a state the plan can actually hold. `null` and absent are the
     * same fact — not recorded — and both raise `cost-basis-missing`.
     */
    costBasis: nonNegativePenceSchema.nullish(),
    /**
     * Member contribution per frequency period, GROSS for DC pensions (the
     * member pays 80% of it; relief at source adds the rest) — entering the
     * net direct-debit amount would overstate the pot by 25% a year.
     */
    contribution: nonNegativePenceSchema,
    contributionFrequency: frequencySchema,
    /**
     * May be negative: a bear-case override or depreciation, floor −100%.
     * Hard-capped at +100%/yr: rates above the 2,000 bps plausibility ceiling
     * are flagged by the engine, rates above this cap are rejected outright —
     * an unbounded override is how £0.9m becomes £11.7bn over 70 years
     * (docs/engine/projection-runaway.md, defect B).
     */
    growthRateBps: bpsSchema
        .min(-10_000, 'Growth cannot be below -10,000 bps (-100%)')
        .max(10_000, 'Growth cannot exceed 10,000 bps (+100%)')
        .nullable(),
    yieldBps: bpsSchema
        .min(0)
        .max(5_000, 'Yield cannot exceed 5,000 bps (+50%)')
        .nullable()
        .optional(),
    feeDragBps: bpsSchema
        .min(0)
        .max(5_000, 'Fees cannot exceed 5,000 bps (+50%)')
        .nullable()
        .optional(),
    contributionEscalation: escalationSchema.optional(),
    property: propertyDetailsSchema.optional(),
    neverDrawFrom: z.boolean().optional(),
    contributionStartYear: yearSchema.nullable().optional(),
    contributionEndYear: yearSchema.nullable().optional(),
    // W1.9 (class fix): `poundsOrBlank`, so CLEARING it writes `null` —
    // "stop the employer contribution" is a live edit, and `.optional()` alone blanked the whole chart.
    employerContribution: nonNegativePenceSchema.nullish(),
    employerContributionFrequency: frequencySchema.optional(),
    accessAge: ageSchema.nullable().optional(),
    /**
     * Which KIND of DC pension this is.
     *
     * `own` (the default, and what every document saved before this field
     * existed means) is the member's own pot: it crystallises at its access
     * age, pays a 25% tax-free lump sum, and cannot be touched before the
     * normal minimum pension age.
     *
     * `beneficiary_drawdown` is a pot INHERITED from someone else. It is
     * already crystallised, so there is no further lump sum to take; it is
     * drawable at ANY age, so the normal minimum pension age does not gate it;
     * and it passes on differently at the beneficiary's own death. Modelling it
     * as an own SIPP overstates the tax-free cash and wrongly locks the money
     * away until 55 — on a plan holding an inherited SIPP that is a large error
     * in both directions at once.
     *
     * OPTIONAL and additive: documents saved before this field existed stay
     * valid and mean `own`.
     */
    dcSubType: z.enum(['own', 'beneficiary_drawdown']).optional(),
    /**
     * The pension product as named (spec §10, N10): a SIPP and a workplace
     * scheme both calculate as defined contribution; this keeps which one it
     * is. Descriptive only — the arithmetic is the wrapper's. DC pensions only.
     */
    pensionProductSubtype: z
        .enum(['sipp', 'workplace', 'personal', 'stakeholder', 'ssas'])
        .optional(),
    dependentId: idSchema.optional(),
    bprRateBps: bpsSchema.min(0).max(10_000).optional(),
    cgtChattelsExempt: z.boolean().optional(),
    /**
     * The figure is an estimate or its basis is unverified — a client's own
     * guess, a valuation nobody has done, a cost basis that cannot be
     * reconstructed. Carried through to the report so an illustrative number is
     * labelled as one instead of being presented with false precision.
     * OPTIONAL and additive: documents saved before this field existed stay valid.
     */
    valueUnverified: z.boolean().optional(),
    notes: z.string().max(2000).optional(),
})
    .strict()
    .refine((a) => a.wrapper === 'jisa' || a.dependentId === undefined, {
    message: 'dependentId applies to Junior ISAs only',
    path: ['dependentId'],
})
    .refine((a) => a.wrapper !== 'jisa' || a.dependentId !== undefined, {
    message: 'A Junior ISA must name its dependent',
    path: ['dependentId'],
})
    .refine((a) => a.wrapper === 'dc_pension' || a.employerContribution === undefined, {
    message: 'Employer contributions apply to DC pensions only',
    path: ['employerContribution'],
})
    .refine((a) => a.wrapper === 'dc_pension' || a.accessAge == null, {
    message: 'accessAge applies to DC pensions only',
    path: ['accessAge'],
})
    .refine((a) => a.wrapper === 'dc_pension' || a.dcSubType === undefined, {
    message: 'dcSubType applies to DC pensions only',
    path: ['dcSubType'],
})
    .refine((a) => a.dcSubType !== 'beneficiary_drawdown' || a.accessAge == null, {
    // An inherited pot is drawable at any age, so an access age would be
    // meaningless at best and would wrongly lock the money away at worst.
    message: 'A beneficiary drawdown pot has no access age — it is drawable at any age',
    path: ['accessAge'],
})
    .refine((a) => a.dcSubType !== 'beneficiary_drawdown' || a.contribution === 0, {
    // Nothing can be paid into an inherited pot.
    message: 'A beneficiary drawdown pot cannot receive contributions',
    path: ['contribution'],
})
    .refine((a) => a.wrapper === 'business' || a.bprRateBps === undefined, {
    message: 'bprRateBps applies to business interests only',
    path: ['bprRateBps'],
})
    .refine((a) => a.wrapper === 'property' || a.property === undefined, {
    message: 'Property details apply to property assets only',
    path: ['property'],
})
    .refine((a) => a.ownerId !== 'joint' ||
    !['cash_isa', 'stocks_isa', 'lisa', 'dc_pension'].includes(a.wrapper), {
    message: 'ISAs, Lifetime ISAs and pensions are individually owned — model one per person',
    path: ['ownerId'],
})
    .refine((a) => a.contribution === 0 ||
    !['property', 'business', 'other_asset', 'db_pension', 'state_pension'].includes(a.wrapper), {
    message: 'Regular contributions are not modelled for this wrapper — use one-off events instead',
    path: ['contribution'],
});
export const liabilityRateChangeSchema = z
    .object({
    fromYear: yearSchema,
    interestRateBps: bpsSchema
        .min(0, 'Enter a rate between 0 and 50%')
        .max(5000, 'Enter a rate between 0 and 50%'),
    monthlyPayment: nonNegativePenceSchema.nullable().optional(),
    interestOnly: z.boolean().optional(),
})
    .strict();
export const householdLiabilitySchema = z
    .object({
    id: idSchema,
    label: labelSchema,
    kind: liabilityTypeSchema,
    balance: nonNegativePenceSchema,
    /**
     * `null` means the adviser has not yet provided a rate — the liability is a
     * visible row in the fact-find but the engine EXCLUDES it from the
     * projection with a standing warning. It is never modelled at 0%: a made-up
     * rate silently entering the arithmetic is exactly the defect this state
     * exists to prevent. `rateChanges` entries keep a required rate — a future
     * change to an unknown rate is not a modellable statement.
     */
    interestRateBps: bpsSchema
        .min(0, 'Enter a rate between 0 and 50%')
        .max(5000, 'Enter a rate between 0 and 50%')
        .nullable(),
    monthlyPayment: nonNegativePenceSchema,
    interestOnly: z.boolean(),
    /**
     * FG-F6-04 (§0.4). Whose debt this is. "Everything else in this tool knows
     * whose it is — income, assets, pensions — and the debts don't. It makes
     * the survivor's cashflow wrong in a meeting where the whole point is the
     * survivor's cashflow." Absent behaves as `joint`, today's behaviour.
     */
    ownerId: z.enum(['client', 'partner', 'joint']).optional(),
    /**
     * FG-F6-04. What happens to it on the owner's death — an explicit adviser
     * choice recorded on the row, never an automatic clearance either way.
     * Absent applies the stated default: settled from the estate for a sole
     * debt, continues for a joint one, and the engine says which it applied.
     */
    onDeath: z.enum(['settledFromEstate', 'continues']).optional(),
    endYear: yearSchema.nullable(),
    startYear: yearSchema.nullable().optional(),
    // W1.9 (class fix): `poundsOrBlank`, so CLEARING it writes `null` —
    // "what if we stop overpaying the mortgage" is a live edit, and `.optional()` alone blanked the whole chart.
    annualOverpayment: nonNegativePenceSchema.nullish(),
    rateChanges: z.array(liabilityRateChangeSchema).max(60).optional(),
})
    .strict();
/**
 * A2.1 gift ledger, shared by the one-off (life event) and recurring (spending
 * line) halves of the plan. The relationship is what sets the marriage-exemption
 * limit; the tool never infers it.
 */
export const giftRelationshipSchema = z.enum([
    'child',
    'grandchild',
    'remoter-descendant',
    'other',
]);
/**
 * A2.8. The adviser's stated position on whether a gift draws the ANNUAL
 * exemption. Absent and `annual` are the same thing, so no saved plan moves.
 * `none` says the adviser knows this gift is covered elsewhere or claims
 * nothing — it draws no exemption AND consumes none, leaving the year's £3,000
 * for the gift that needs it. The marriage exemption is orthogonal and is
 * governed by `giftOccasion`.
 *
 * `normalExpenditureOutOfIncome` (A2.2) is the third value, and it is not like
 * the other two: it takes the gift out of the seven-year clock ALTOGETHER — no
 * taper, no nil-rate band, and no annual exemption spent, because it is exempt
 * on a different basis (IHTA 1984 s.21). It was deliberately refused by the
 * contract until the engine implemented it, so that an adviser's stated
 * position could never be accepted and silently ignored (D-009 A2.7/A2.8
 * addendum); taking it now requires `normalExpenditureNote` alongside.
 */
/**
 * A2.4 (FG-F4-02). Two separate facts about who pays education fees, kept
 * deliberately apart because they answer different questions.
 *
 * `paidBy` is about this household's CASH FLOW: fees somebody else pays are not
 * an outflow here, and modelling them as one depletes a plan that is fine.
 *
 * `educationIhtTreatment` is about the seven-year clock when this household IS
 * paying for someone outside it — the case half of fee-paying enquiries are
 * actually about. It records the adviser's stated position and evaluates
 * nothing, reusing A2.2's vocabulary rather than inventing a parallel one:
 * `normalExpenditureOutOfIncome` here means exactly what it means on a gift,
 * and carries the same required note.
 *
 * `notAssessed` is the default and today's behaviour — the fees are spending
 * and reach no gift — but the engine now SAYS so rather than staying silent.
 */
export const educationPaidBySchema = z.enum(['household', 'thirdParty']);
export const educationIhtTreatmentSchema = z.enum([
    'notAssessed',
    'gift',
    'normalExpenditureOutOfIncome',
]);
export const exemptionClaimedSchema = z.enum(['annual', 'none', 'normalExpenditureOutOfIncome']);
/**
 * A2.2. The adviser's free-text record of WHY they hold the normal-expenditure
 * position — required whenever they take it (D-009 N-A2). The tool cannot test
 * s.21's conditions (a pattern of giving, made out of income, leaving the usual
 * standard of living intact) and does not try; requiring the note is what stops
 * the most powerful exemption in the ledger from being a box someone ticks.
 */
export const normalExpenditureNoteSchema = z.string().trim().min(10).max(1000);
/**
 * B2.1 — one recorded-not-modelled arrangement. `kind` and `role` are closed
 * enumerations because they drive the fixed wording the client reads; `note` is
 * the adviser's own free text and reaches no client surface.
 */
export const householdTrustSchema = z
    .object({
    id: idSchema,
    label: labelSchema,
    kind: z.enum([
        'bare',
        'discretionary',
        'interestInPossession',
        'loanTrust',
        'discountedGiftTrust',
        'other',
    ]),
    role: z.enum(['settlor', 'trustee', 'beneficiary']),
    /**
     * B2.1 review (tech-admin): `.min(10)` like its A2.2 precedent. Without it
     * the contract accepted `""` and `"   "` and persisted the key — a stored
     * field that says nothing, on a record whose whole purpose is to say
     * something.
     */
    note: z.string().trim().min(10).max(1000).optional(),
})
    .strict();
export const householdSpendingItemSchema = z
    .object({
    id: idSchema,
    label: labelSchema,
    category: spendingCategorySchema.optional(),
    amount: nonNegativePenceSchema,
    frequency: frequencySchema,
    startYear: yearSchema.nullable(),
    endYear: yearSchema.nullable(),
    inflationLinked: z.boolean(),
    /**
     * FG-F3-04 (§0.4 makes ownership universal). A row owned by one person
     * stops when they die; a `joint` row scales by the household's stated
     * survivor factor. Absent behaves as `joint`, which is today's behaviour.
     *
     * A2.5a review round 2 (tech-admin): `household` is the engine's
     * never-scaled value — a cost that is neither a person's nor the couple's
     * shared living cost, such as fees paid for a grandchild — and it was not
     * on the wire, so the adviser had no way to say it and the survivor factor
     * shrank third-party costs it has no business touching.
     */
    ownerId: z.enum(['client', 'partner', 'joint', 'household']).optional(),
    escalation: escalationSchema.optional(),
    /**
     * The OTHER figure captured for this line, when a fact-find holds two —
     * typically the client's own estimate alongside a statement-derived figure.
     * A household's estimate is commonly well below what their statements show,
     * and which basis the plan uses changes the answer, so both are kept and
     * `amountBasis` records which one `amount` is.
     */
    amountAlternative: nonNegativePenceSchema.nullable().optional(),
    /** Which basis `amount` holds. Absent means the basis was not recorded. */
    amountBasis: z.enum(['clientEstimate', 'statementDerived']).optional(),
    /**
     * A2.7. This recurring outflow is a GIFT, so each year it runs reaches the
     * seven-year clock at the amount actually given that year. "£500 a month to
     * the grandchildren" is one record here, not ten life events — and not, as
     * before, a spending line that reduced the wealth on the chart while the
     * gifts page said no gifts had been recorded. The flag moves no cash flow:
     * the money already left the plan.
     */
    isGift: z.boolean().optional(),
    giftRecipient: labelSchema.optional(),
    /** A2.2: required when `exemptionClaimed` is `normalExpenditureOutOfIncome`. */
    normalExpenditureNote: normalExpenditureNoteSchema.optional(),
    /**
     * A2.7 review (tech-admin): `giftRelationship` is deliberately NOT here.
     * The relationship exists to set the marriage-exemption limit, and a
     * marriage happens once — so it means nothing on a recurring line. The
     * first cut accepted it, mapped it into the engine and read it nowhere,
     * which is the silently-ignored input the refine below exists to prevent,
     * and one more piece of third-party-adjacent data under the open A2.13.
     */
    exemptionClaimed: exemptionClaimedSchema.optional(),
})
    .strict()
    .refine((s) => s.amountAlternative == null || s.amountBasis !== undefined, {
    message: 'Recording a second figure requires saying which basis the plan is using',
    path: ['amountBasis'],
})
    .refine((s) => s.exemptionClaimed !== 'normalExpenditureOutOfIncome' ||
    s.normalExpenditureNote !== undefined, {
    // D-009 N-A2: the note is required, not encouraged. This exemption is the
    // one the tool cannot check at all, so the adviser's reasoning is the only
    // record that it was reasoned about.
    message: 'Taking the normal-expenditure position requires a note recording the basis for it',
    path: ['normalExpenditureNote'],
})
    .refine((s) => s.exemptionClaimed === 'normalExpenditureOutOfIncome' ||
    s.normalExpenditureNote === undefined, {
    // A2.2 review (tech-admin): this checked `!== undefined`, so the note —
    // the field most likely to carry a third party's income, health or
    // circumstances — attached happily to an `annual` or `none` claim, where
    // nothing reads it and it is kept forever in the run snapshot. The error
    // message already said what the rule was; the rule did not.
    message: 'A normal-expenditure note applies only where that exemption is claimed',
    path: ['normalExpenditureNote'],
})
    .refine((s) => s.isGift === true ||
    (s.giftRecipient === undefined &&
        s.exemptionClaimed === undefined &&
        s.normalExpenditureNote === undefined), {
    // A recipient held against a line that is not a gift is third-party
    // personal data kept for nothing, and an input the engine would ignore
    // without saying so.
    message: 'Gift details apply to a spending line marked as a gift',
    path: ['isGift'],
});
export const householdLifeEventSchema = z
    .object({
    id: idSchema,
    label: labelSchema,
    year: yearSchema,
    amount: nonNegativePenceSchema,
    direction: z.enum(['inflow', 'outflow', 'none']),
    wrapper: wrapperSchema.nullable(),
    isGift: z.boolean().optional(),
    /**
     * A2.1 gift ledger. Who received it and how they are related to the giver
     * — the relationship is what sets the marriage-exemption limit, and the
     * recipient is what lets the report say who a gift went to instead of
     * listing anonymous outflows. `giftOccasion: 'wedding'` is the adviser
     * ASSERTING the occasion; the tool never infers it.
     */
    giftRecipient: labelSchema.optional(),
    giftRelationship: giftRelationshipSchema.optional(),
    giftOccasion: z.enum(['wedding']).optional(),
    /** A2.8: whether this gift draws the annual exemption. See `exemptionClaimedSchema`. */
    exemptionClaimed: exemptionClaimedSchema.optional(),
    /** A2.2: required when `exemptionClaimed` is `normalExpenditureOutOfIncome`. */
    normalExpenditureNote: normalExpenditureNoteSchema.optional(),
    bprRateBps: bpsSchema.min(0).max(10_000).optional(),
})
    .strict()
    .refine((e) => e.exemptionClaimed !== 'normalExpenditureOutOfIncome' ||
    e.normalExpenditureNote !== undefined, {
    message: 'Taking the normal-expenditure position requires a note recording the basis for it',
    path: ['normalExpenditureNote'],
})
    .refine((e) => e.exemptionClaimed === 'normalExpenditureOutOfIncome' ||
    e.normalExpenditureNote === undefined, {
    message: 'A normal-expenditure note applies only where that exemption is claimed',
    path: ['normalExpenditureNote'],
})
    .refine((e) => e.isGift === true ||
    (e.exemptionClaimed === undefined &&
        e.giftRecipient === undefined &&
        e.giftRelationship === undefined &&
        e.giftOccasion === undefined &&
        e.normalExpenditureNote === undefined), {
    // A2.12, closed here rather than left open: the first cut gated only the
    // field this change added, which made the two halves of the ledger
    // disagree about the same personal data — a spending line rejected a
    // recipient on a non-gift while a life event accepted one, plus a
    // wedding occasion, and ignored both.
    message: 'Gift details apply to an event marked as a gift',
    path: ['isGift'],
});
export const ihtSettingsSchema = z
    .object({
    residenceToDescendants: nonNegativePenceSchema.optional(),
    passesToSpouse: z.boolean().optional(),
    hasFullTransferredAllowances: z.boolean().optional(),
    charityRateApplies: z.boolean().optional(),
    pensionsOutsideEstate: z.boolean().optional(),
    /**
     * A2.8. What the annual gift exemption in the tax year IMMEDIATELY BEFORE
     * the plan's earliest recorded gift was already used for. The exemption
     * walk has to start somewhere, and it started by assuming that year
     * entirely unused — carrying a full £3,000 into the plan out of a year the
     * plan holds no record of, in the client's favour, silently. Stating it
     * here replaces the assumption; leaving it unstated keeps the assumption
     * and raises `gift-exemption-pre-plan-unused` (A2.14) so it is at least
     * said out loud.
     */
    priorYearExemptionUsed: nonNegativePenceSchema.optional(),
    /**
     * GOAL 2026-09-16 IE-2. What the residence nil-rate band is measured
     * against — kept apart from `residenceToDescendants` so a blank never
     * silently means "confirmed zero":
     *
     *  - absent: the pre-IE-2 reading — `residenceToDescendants` when present
     *    (a fixed amount), otherwise NOT ENTERED;
     *  - `doesNotApply`: the adviser has stated no residence passes to direct
     *    descendants;
     *  - `recordedMainResidence`: the plan's main residence is stated to pass to
     *    direct descendants, valued each year at its PROJECTED value less the
     *    balance of its linked mortgage — so a 2045 estimate never uses today's
     *    price;
     *  - `fixedAmount`: `residenceToDescendants` as entered, not projected.
     *
     * It records the adviser's stated position; whether the interest is
     * "closely inherited" is not assessed by this tool.
     */
    residenceBasis: z.enum(['doesNotApply', 'recordedMainResidence', 'fixedAmount']).optional(),
    /**
     * IE-2. The share of a late spouse's or civil partner's nil-rate band left
     * unused at their death, per band, in basis points (IHTA 1984 s.8A; s.8G),
     * capped at 100%. An alternative to `hasFullTransferredAllowances`, which is
     * exactly 10,000/10,000 — the two may not both be given.
     */
    transferredNilRateBandBps: bpsSchema.min(0).max(10_000).optional(),
    transferredResidenceNilRateBandBps: bpsSchema.min(0).max(10_000).optional(),
})
    .strict()
    /**
     * IE review (tech-admin). This refinement sits in the READ path too — the
     * stored-snapshot schemas reuse `ihtSettingsSchema` — which the single-main-
     * residence rule deliberately avoids (see its READ/WRITE note). It is safe
     * only because every check below involves a field that did not exist before
     * IE-2, so no document or snapshot saved earlier can trip it. Any future
     * tightening that could reject an EXISTING field must move to the write
     * schemas instead, or stored runs stop replaying.
     */
    .superRefine((i, ctx) => {
    const percentages = i.transferredNilRateBandBps !== undefined ||
        i.transferredResidenceNilRateBandBps !== undefined;
    if (i.hasFullTransferredAllowances === true && percentages) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['hasFullTransferredAllowances'],
            message: 'Record the transferred allowances either as full or as percentages for each band, not both',
        });
    }
    if (i.residenceBasis === 'fixedAmount' && i.residenceToDescendants === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['residenceToDescendants'],
            message: 'Enter the value of the home passing to direct descendants',
        });
    }
    if ((i.residenceBasis === 'doesNotApply' || i.residenceBasis === 'recordedMainResidence') &&
        i.residenceToDescendants !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['residenceToDescendants'],
            message: 'A fixed residence value applies only when the residence allowance is recorded as a fixed amount',
        });
    }
});
/**
 * One destination for surplus cash flow. `jisa` (client goals, 2026-09-03) is a
 * FUND-IF-SURPLUS step: it credits the named dependent's Junior ISA at the
 * engine's surplus step (step 10) — never at contributions (step 6) — so it
 * is paid only in a year that has room for it after tax, all spending, debt
 * service and every unconditional contribution, and it never draws on a pot.
 * The statutory per-child limit is shared with any step-6 subscription to the
 * same child. `dependentId` is required iff the target is `jisa`; `goalId`
 * links the step back to the `goals[]` record that placed it, on any target.
 */
export const surplusSweepTargetSchema = z.enum([
    'isa',
    'pension',
    'gia',
    'cash',
    'jisa',
    'spend',
    'liability',
]);
export const surplusWaterfallStepSchema = z
    .object({
    target: surplusSweepTargetSchema,
    annualCap: nonNegativePenceSchema.nullable(),
    targetBalanceMonthsOfSpending: z.number().int().min(0).max(120).nullable().optional(),
    targetBalance: nonNegativePenceSchema.nullable().optional(),
    dependentId: idSchema.optional(),
    goalId: idSchema.optional(),
    /** `pension` steps: restrict the placement to one person (a goal's "extra into MY pension"). */
    personId: personIdSchema.optional(),
    /** `liability` steps (v2): the debt an overpayment goes to. */
    liabilityId: idSchema.optional(),
    /** `spend` steps (v2): what the money is for, as the client said it. */
    label: labelSchema.optional(),
    /** `spend` steps: a gift (feeds the inheritance-tax gift list), not consumption. */
    isGift: z.boolean().optional(),
})
    .strict()
    .refine((s) => s.target !== 'jisa' || s.dependentId !== undefined, {
    message: 'A Junior ISA sweep step must name the dependent whose account it funds',
    path: ['dependentId'],
})
    .refine((s) => s.target === 'jisa' || s.dependentId === undefined, {
    message: 'dependentId applies to Junior ISA sweep steps only',
    path: ['dependentId'],
})
    .refine((s) => s.target !== 'liability' || s.liabilityId !== undefined, {
    message: 'An overpayment step must name the debt it pays down',
    path: ['liabilityId'],
})
    .refine((s) => s.target === 'liability' || s.liabilityId === undefined, {
    message: 'liabilityId applies to overpayment steps only',
    path: ['liabilityId'],
})
    .refine((s) => s.target !== 'spend' || s.annualCap !== null, {
    message: 'A spend step must state the amount',
    path: ['annualCap'],
});
export const surplusWaterfallSchema = z
    .object({
    steps: z.array(surplusWaterfallStepSchema).min(1).max(20),
})
    .strict();
/**
 * The opt-in surplus sweep, on the wire.
 *
 * Surplus cash flow accumulates as CASH unless a plan says otherwise — sweeping
 * it into tax wrappers is a course of action, and doing it by default put a
 * recommendation inside a modelling aid. This is the shape a plan states when
 * an adviser deliberately chooses the sweep, and it is the single source the
 * builder, migration 0024 and the engine's `SURPLUS_SWEEP_WATERFALL` all agree
 * on (asserted by a drift test).
 */
export const SURPLUS_SWEEP_WATERFALL_WIRE = Object.freeze({
    steps: Object.freeze([
        Object.freeze({ target: 'cash', annualCap: null, targetBalanceMonthsOfSpending: 6 }),
        Object.freeze({ target: 'isa', annualCap: null }),
        Object.freeze({ target: 'pension', annualCap: null }),
        Object.freeze({ target: 'gia', annualCap: null }),
    ]),
});
export const withdrawalWaterfallSchema = z
    .object({
    steps: z
        .array(z
        .object({
        target: z.enum(['cash', 'cash_isa', 'stocks_isa', 'isa', 'gia', 'pension']),
    })
        .strict())
        .min(1)
        .max(20),
})
    .strict();
export const projectionWindowSchema = z
    .object({
    endYear: yearSchema.nullable().optional(),
    endAtClientAge: ageSchema.nullable().optional(),
})
    .strict();
/** Per-wrapper bps overrides (partial: only the wrappers the adviser sets). */
const bpsByWrapperSchema = z
    .object(Object.fromEntries(Object.values(Wrapper).map((w) => [w, bpsSchema.min(-10_000).optional()])))
    .strict();
/** Wrappers a shock may target: cash is excluded (its interest is tax-aligned
 * at source — a rate change belongs in growth overrides), and DB/state
 * pensions hold no modelled capital. The engine re-refuses these (fail-closed
 * twice). */
export const SHOCKABLE_WRAPPERS = Object.freeze(Object.values(Wrapper).filter((w) => w !== Wrapper.Cash && w !== Wrapper.DbPension && w !== Wrapper.StatePension));
/**
 * A dated window during which the named economic assumptions replace the base
 * values (e.g. "inflation 5% from 2026 to 2030, then back to the base"), and
 * per-wrapper growth is ADJUSTED by a delta (e.g. "returns 3% lower for the
 * first five years"). Steps may overlap; the LAST matching step wins. Cash and
 * the pension promises are not growth-adjustable (cash interest is tax-aligned
 * at source; DB/state pensions hold no modelled capital).
 */
export const assumptionScheduleStepSchema = z
    .object({
    id: idSchema,
    label: labelSchema.optional(),
    fromYear: yearSchema,
    /** null/absent = open-ended (to the end of the projection). */
    toYear: yearSchema.nullable().optional(),
    inflationBps: bpsSchema.min(0).max(2000).optional(),
    wageGrowthBps: bpsSchema.min(0).max(2000).optional(),
    rpiBps: bpsSchema.min(0).max(2000).optional(),
    growthDeltaBpsByWrapper: z
        .object(Object.fromEntries(SHOCKABLE_WRAPPERS.map((w) => [w, bpsSchema.min(-10_000).max(10_000).optional()])))
        .strict()
        .optional(),
})
    .strict()
    .superRefine((step, ctx) => {
    if (step.toYear != null && step.toYear < step.fromYear) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['toYear'],
            message: 'The window ends before it starts',
        });
    }
    const hasScalar = step.inflationBps !== undefined ||
        step.wageGrowthBps !== undefined ||
        step.rpiBps !== undefined;
    const hasGrowth = Object.values(step.growthDeltaBpsByWrapper ?? {}).some((v) => v !== undefined);
    if (!hasScalar && !hasGrowth) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A timed change must set at least one assumption',
        });
    }
});
/**
 * The assumptions SPEC (preset + overrides), not raw resolved rates: the API
 * resolves it via the engine's `resolveAssumptions` front door so every run
 * carries full provenance (Stage 2 contract).
 */
export const globalOverridesSchema = z
    .object({
    inflationBps: bpsSchema.min(0).max(2000).optional(),
    rpiBps: bpsSchema.min(0).max(2000).optional(),
    wageGrowthBps: bpsSchema.min(0).max(2000).optional(),
    /** Dated assumption windows (last matching step wins per year). */
    schedule: z.array(assumptionScheduleStepSchema).max(20).optional(),
    growthByWrapper: bpsByWrapperSchema.optional(),
    yieldByWrapper: bpsByWrapperSchema.optional(),
    feeByWrapper: bpsByWrapperSchema.optional(),
    statePensionEscalation: statePensionEscalationSchema.optional(),
    /** Named custom inflation indices (bps) an item's escalation can link to
     * via `{ kind: 'indexPlus', index }` (FEATURES §1.1). */
    customIndices: z.record(indexNameSchema, bpsSchema.min(-10_000).max(10_000)).optional(),
})
    .strict();
export const assumptionSpecSchema = z
    .object({
    preset: z.enum(['conservative', 'moderate', 'optimistic', 'custom']),
    /**
     * An exact PUBLISHED firm assumption version this plan is pinned to (firm
     * assumptions spec §2.2 PlanAssumptionSelection). When present it supplies
     * every base figure and `preset` is carried only for older readers; the
     * server resolves the id inside the caller's tenant transaction, so an id
     * from another firm, a draft, or a missing version is refused — never
     * replaced by a product bundle. Never "latest": a later version of the
     * same set does not move this plan until it is explicitly adopted.
     */
    setVersionId: z.string().uuid().optional(),
    portfolio: z.string().trim().min(1).max(120).optional(),
    horizonYears: z.number().int().min(1).max(90).optional(),
    overrides: globalOverridesSchema.optional(),
})
    .strict();
// --- planned events (Stage 5) ------------------------------------------------
const planBase = {
    id: idSchema,
    label: labelSchema,
};
/** Set when a figure came from a named preset — provenance survives the wire. */
const presetIdSchema = z.string().trim().min(1).max(60).nullable().optional();
const plannedEventUnion = z.discriminatedUnion('kind', [
    z
        .object({
        ...planBase,
        kind: z.literal('schoolFees'),
        dependentId: idSchema,
        feePerTerm: nonNegativePenceSchema,
        presetId: presetIdSchema,
        termsPerYear: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
        startAge: ageSchema.nullable().optional(),
        startYear: yearSchema.nullable().optional(),
        endAge: ageSchema.nullable().optional(),
        endYear: yearSchema.nullable().optional(),
        escalation: escalationSchema.optional(),
        siblingDiscountBps: bpsSchema.min(0).max(10_000).optional(),
        paidBy: educationPaidBySchema.optional(),
        /**
         * A2.4 review: `'annual' | 'none'` only. The s.21 position on an
         * education plan is `ihtTreatment`, and offering it twice would be two
         * places for one fact to drift — the thing A2.2 refused to do when it
         * declined to put `wedding` on this enum.
         */
        exemptionClaimed: z.enum(['annual', 'none']).optional(),
        ihtTreatment: educationIhtTreatmentSchema.optional(),
        normalExpenditureNote: normalExpenditureNoteSchema.optional(),
    })
        .strict(),
    z
        .object({
        ...planBase,
        kind: z.literal('university'),
        dependentId: idSchema,
        annualTuition: nonNegativePenceSchema,
        annualMaintenance: nonNegativePenceSchema,
        presetId: presetIdSchema,
        durationYears: z.number().int().min(1).max(8).optional(),
        startAge: ageSchema.nullable().optional(),
        startYear: yearSchema.nullable().optional(),
        parentalContributionBps: bpsSchema.min(0).max(10_000).optional(),
        escalation: escalationSchema.optional(),
        paidBy: educationPaidBySchema.optional(),
        /**
         * A2.4 review: `'annual' | 'none'` only. The s.21 position on an
         * education plan is `ihtTreatment`, and offering it twice would be two
         * places for one fact to drift — the thing A2.2 refused to do when it
         * declined to put `wedding` on this enum.
         */
        exemptionClaimed: z.enum(['annual', 'none']).optional(),
        ihtTreatment: educationIhtTreatmentSchema.optional(),
        normalExpenditureNote: normalExpenditureNoteSchema.optional(),
    })
        .strict(),
    z
        .object({
        ...planBase,
        kind: z.literal('propertyPurchase'),
        year: yearSchema,
        price: nonNegativePenceSchema,
        deposit: nonNegativePenceSchema,
        ownerId: assetOwnerSchema.optional(),
        isPrimaryResidence: z.boolean().optional(),
        isBuyToLet: z.boolean().optional(),
        rentalYieldBps: bpsSchema.min(0).max(2000).optional(),
        growthRateBps: bpsSchema.min(-10_000).max(10_000).nullable().optional(),
        isFirstTimeBuyer: z.boolean().optional(),
        isAdditionalDwelling: z.boolean().optional(),
        mortgageRateBps: bpsSchema.min(0).max(5000),
        mortgageMonthlyPayment: nonNegativePenceSchema,
        mortgageInterestOnly: z.boolean().optional(),
        mortgageEndYear: yearSchema.nullable().optional(),
    })
        .strict(),
    z
        .object({
        ...planBase,
        kind: z.literal('propertySale'),
        year: yearSchema,
        propertyId: idSchema,
        sellingCostsBps: bpsSchema.min(0).max(1000).optional(),
    })
        .strict(),
    z
        .object({
        ...planBase,
        kind: z.literal('businessSale'),
        year: yearSchema,
        assetId: idSchema,
        badrQualifying: z.boolean().optional(),
        destination: z.enum(['cash', 'gia']).optional(),
    })
        .strict(),
    z
        .object({
        ...planBase,
        kind: z.literal('careerBreak'),
        personId: personIdSchema,
        startYear: yearSchema.nullable().optional(),
        startAge: ageSchema.nullable().optional(),
        durationYears: z.number().int().min(1).max(50),
        // W1.9 (class fix): `poundsOrBlank`, so CLEARING it writes `null` —
        // a cleared redundancy figure is a live edit, and `.optional()` alone blanked the whole chart.
        redundancyLumpSum: nonNegativePenceSchema.nullish(),
    })
        .strict(),
    z
        .object({
        ...planBase,
        kind: z.literal('care'),
        personId: personIdSchema,
        startAge: ageSchema.nullable().optional(),
        startYear: yearSchema.nullable().optional(),
        durationYears: z.number().int().min(1).max(60).nullable().optional(),
        annualCost: nonNegativePenceSchema,
        presetId: presetIdSchema,
        escalation: escalationSchema.optional(),
    })
        .strict(),
    z
        .object({
        ...planBase,
        kind: z.literal('recurring'),
        amount: nonNegativePenceSchema,
        everyNYears: z.number().int().min(1).max(50),
        startYear: yearSchema,
        endYear: yearSchema.nullable().optional(),
        direction: z.enum(['outflow', 'inflow']).optional(),
        escalation: escalationSchema.optional(),
    })
        .strict(),
]);
// --- market shocks (scenario stress tests) -----------------------------------
const shockDropBps = z
    .number()
    .int()
    .min(1, 'A shock drop must be at least 1 bp')
    .max(10_000, 'A shock drop cannot exceed 10,000 bps (100%)');
/** Signed per-wrapper return delta for one year of a stress path (−100%..+100%). */
const shockDeltaBpsByWrapper = z
    .object(Object.fromEntries(SHOCKABLE_WRAPPERS.map((w) => [w, bpsSchema.min(-10_000).max(10_000).optional()])))
    .strict();
/**
 * Where a stress path starts (GOAL 2026-09-05 C1.1): a calendar year, or N
 * years after a person's retirement — resolved by the engine once, after any
 * scenario overlay has moved that retirement age, so the window follows the
 * retirement it names.
 */
export const marketShockAnchorSchema = z.union([
    z.object({ year: yearSchema }).strict(),
    z
        .object({
        yearsAfterRetirement: z.number().int().min(0).max(50),
        person: z.enum(['client', 'partner', 'firstToRetire']),
    })
        .strict(),
]);
/**
 * HS-2 (GOAL 2026-09-17): a year's TOTAL return per wrapper, replacing the
 * plan's effective growth, yield and timed growth delta for that year. The floor
 * is a genuine −100% for unleveraged capital; the ceiling is +1,000% so sourced
 * years above +100% (1975 UK equities) pass unclipped.
 */
const shockReturnBpsByWrapper = z
    .object(Object.fromEntries(SHOCKABLE_WRAPPERS.map((w) => [w, bpsSchema.min(-10_000).max(100_000).optional()])))
    .strict();
/** Sourced-observation bounds: explicit, and a value outside them is refused (never clipped). */
const economyRateBps = bpsSchema
    .min(-2000, 'A sourced rate below −20% is outside the supported range')
    .max(5000, 'A sourced rate above 50% is outside the supported range');
/**
 * HS-2: one year's economy in a historical replay. Each value REPLACES that
 * year's assumption. `rpiBps: null` records that no RPI exists for the year —
 * an RPI-linked amount then refuses the projection instead of borrowing a proxy.
 */
export const marketShockEconomySchema = z
    .object({
    inflationBps: economyRateBps.optional(),
    rpiBps: economyRateBps.nullable().optional(),
    wageGrowthBps: economyRateBps.optional(),
    cashRateBps: bpsSchema.min(-500).max(3000).optional(),
})
    .strict();
export const marketShockPathStepSchema = z
    .object({
    offsetYears: z.number().int().min(0).max(30),
    returnDeltaBpsByWrapper: shockDeltaBpsByWrapper,
    returnBpsByWrapper: shockReturnBpsByWrapper.optional(),
    economy: marketShockEconomySchema.optional(),
})
    .strict();
/** HS-2: a contract-aware rate path for one liability (offsets from the shock's anchor). */
export const liabilityRatePathSchema = z
    .object({
    liabilityId: idSchema,
    reamortise: z.boolean(),
    steps: z
        .array(z
        .object({
        offsetYears: z.number().int().min(0).max(40),
        interestRateBps: bpsSchema.min(0).max(5000),
    })
        .strict())
        .min(1)
        .max(41),
})
    .strict();
/**
 * HS-2: which dataset a replay was built from, stored with the scenario so a
 * later revision of the figures cannot silently change what a past run meant.
 */
export const marketShockDatasetSchema = z
    .object({
    id: z.string().trim().min(1).max(80),
    version: z.string().trim().min(1).max(40),
    contentHash: z.string().regex(/^[0-9a-f]{16}$/),
    /**
     * What the replay assumed about THIS household (mix, earnings, RPI, each
     * mortgage) and the sources' attribution, in the words shown to the
     * adviser — kept with the scenario so the result can be explained later and
     * printed in the report. Written by the replay, never typed.
     */
    appliedAs: z.string().trim().min(1).max(4000),
})
    .strict();
/**
 * A market shock — a stress input, never a prediction. Two forms:
 *
 * - the original one-year fall: `year` + `dropBpsByWrapper` (2000 = a 20%
 *   fall). Every stored snapshot carries this form and it is treated as a
 *   single-element negative path, byte-identical to before;
 * - a SIGNED multi-year `path` from an `anchor` (C1.1): each step's
 *   `returnDeltaBpsByWrapper` is added to that year's growth rate, so a fall
 *   is negative and a recovery year positive.
 *
 * Shocks landing in the same year stack; the engine clamps every write-down so
 * no pot goes below zero.
 */
export const marketShockSchema = z
    .object({
    id: idSchema,
    label: labelSchema,
    year: yearSchema.optional(),
    dropBpsByWrapper: z
        .object(Object.fromEntries(SHOCKABLE_WRAPPERS.map((w) => [w, shockDropBps.optional()])))
        .strict()
        .optional(),
    anchor: marketShockAnchorSchema.optional(),
    path: z.array(marketShockPathStepSchema).min(1).max(31).optional(),
    liabilityRatePaths: z.array(liabilityRatePathSchema).max(20).optional(),
    dataset: marketShockDatasetSchema.optional(),
})
    .strict()
    .superRefine((shock, ctx) => {
    const legacy = shock.year !== undefined || shock.dropBpsByWrapper !== undefined;
    if (legacy && (shock.liabilityRatePaths !== undefined || shock.dataset !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['liabilityRatePaths'],
            message: 'Rate paths and a dataset belong to an anchored path, not a one-year fall',
        });
    }
    const pathForm = shock.anchor !== undefined || shock.path !== undefined;
    if (legacy === pathForm) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['path'],
            message: 'A market shock is either a one-year fall (year + dropBpsByWrapper) or an anchored path (anchor + path), not both and not neither',
        });
        return;
    }
    if (legacy) {
        if (shock.year === undefined || shock.dropBpsByWrapper === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['year'],
                message: 'A one-year fall needs both a year and dropBpsByWrapper',
            });
            return;
        }
        if (Object.values(shock.dropBpsByWrapper).every((v) => v === undefined)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dropBpsByWrapper'],
                message: 'A market shock must name at least one wrapper',
            });
        }
        return;
    }
    if (shock.anchor === undefined || shock.path === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['anchor'],
            message: 'An anchored path needs both an anchor and a path',
        });
        return;
    }
    const offsets = new Set();
    shock.path.forEach((step, i) => {
        if (offsets.has(step.offsetYears)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['path', i, 'offsetYears'],
                message: 'Each path step must be a different year',
            });
        }
        offsets.add(step.offsetYears);
        const adjusts = Object.values(step.returnDeltaBpsByWrapper).some((v) => v !== undefined);
        const replaces = Object.values(step.returnBpsByWrapper ?? {}).some((v) => v !== undefined);
        const economy = Object.values(step.economy ?? {}).some((v) => v !== undefined);
        if (!adjusts && !replaces && !economy) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['path', i, 'returnDeltaBpsByWrapper'],
                message: 'Each path step must name at least one wrapper',
            });
        }
        for (const [w, v] of Object.entries(step.returnBpsByWrapper ?? {})) {
            if (v !== undefined && step.returnDeltaBpsByWrapper[w] !== undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['path', i, 'returnBpsByWrapper', w],
                    message: 'A year cannot both replace and adjust the same return — it would apply twice',
                });
            }
        }
    });
});
/** Cross-field rules enforced at the wire (field-level 422s), then re-checked
 * by the engine's own validate() — fail closed twice. */
export const plannedEventSchema = plannedEventUnion.superRefine((plan, ctx) => {
    if (plan.kind === 'propertyPurchase' && plan.deposit > plan.price) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['deposit'],
            message: 'The deposit cannot exceed the purchase price',
        });
    }
    if (plan.kind === 'schoolFees') {
        if (plan.startYear != null && plan.endYear != null && plan.endYear < plan.startYear) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['endYear'],
                message: 'The window ends before it starts',
            });
        }
        if (plan.startAge != null && plan.endAge != null && plan.endAge < plan.startAge) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['endAge'],
                message: 'The window ends before it starts',
            });
        }
    }
    /**
     * A2.4. The note rules live in the union's own `superRefine` rather than on
     * the two members: `z.discriminatedUnion` needs plain objects, and a
     * `.refine()` on a member turns it into a ZodEffects that the discriminator
     * cannot read. Same rule as A2.2's gift ledger, same wording.
     */
    if (plan.kind === 'schoolFees' || plan.kind === 'university') {
        /**
         * A2.4 review (adviser): `paidBy: 'thirdParty'` with `ihtTreatment: 'gift'`
         * validated and then silently dropped the gift — fees somebody else pays
         * cannot be a gift made by THIS household. The note fields got a rule and
         * this pairing did not.
         */
        if (plan.paidBy === 'thirdParty' &&
            plan.ihtTreatment !== undefined &&
            plan.ihtTreatment !== 'notAssessed') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['ihtTreatment'],
                message: 'Fees paid by someone else are not a gift made by this household — record a treatment only where this household pays',
            });
        }
        if (plan.ihtTreatment === 'normalExpenditureOutOfIncome' &&
            plan.normalExpenditureNote === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['normalExpenditureNote'],
                message: 'Taking the normal-expenditure position requires a note recording the basis for it',
            });
        }
        if (plan.ihtTreatment !== 'normalExpenditureOutOfIncome' &&
            plan.normalExpenditureNote !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['normalExpenditureNote'],
                message: 'A normal-expenditure note applies only where that exemption is claimed',
            });
        }
    }
    if (plan.kind === 'recurring' && plan.endYear != null && plan.endYear < plan.startYear) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endYear'],
            message: 'The window ends before it starts',
        });
    }
    if (plan.kind === 'careerBreak' || plan.kind === 'care') {
        const anchors = [plan.startYear, plan.startAge].filter((a) => a != null);
        if (anchors.length !== 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['startYear'],
                message: 'Give exactly one anchor: startYear or startAge',
            });
        }
    }
});
/**
 * A household has at most ONE main residence. The flag is not cosmetic: it
 * decides which property sale is relieved from CGT under private-residence
 * relief and which property carries the residence nil-rate band, so two of
 * them would make the projection ambiguous. Shared by every schema below that
 * carries a whole asset list, and re-applied after `.omit()`/`.extend()`
 * because those return a plain object schema without the refinement.
 *
 * READ/WRITE ASYMMETRY, deliberate: this rule constrains what may be WRITTEN,
 * not what may be READ BACK. Documents saved before the rule existed can hold
 * two flagged properties (every property used to default to the flag), and an
 * immutable `inputs_snapshot` on a historical run can never be edited at all.
 * Applying the refine to those reads would lock an adviser out of the client
 * and permanently break replay of stored runs, so the `*StoredSchema` variants
 * below parse them unrefined; the builder demotes the extras on load and the
 * next write goes through the strict schema.
 */
function refineSingleMainResidence(doc, ctx) {
    const marked = [];
    doc.assets.forEach((asset, i) => {
        if (asset.property?.isPrimaryResidence === true)
            marked.push(i);
    });
    if (marked.length <= 1)
        return;
    for (const i of marked) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['assets', i, 'property', 'isPrimaryResidence'],
            message: `Only one property can be the main residence — ${marked.length} are marked. Turn this off on every property except the home the household lives in.`,
        });
    }
}
// --- client goals (2026-09-03 brief) ------------------------------------------
/**
 * WHEN a goal is measured, or when a fund-if-surplus goal stops (FEATURES
 * §0.2): a person's age, ANY dependent's age ("when Dan is 18"), or a
 * calendar year. Ages resolve through the engine's own age rule at run time,
 * never through a default year — a dependent with no usable date of birth
 * makes the goal "outside the projection", not anchored to a guess.
 */
export const goalAnchorSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('personAge'), personId: personIdSchema, age: ageSchema }).strict(),
    z.object({ kind: z.literal('dependentAge'), dependentId: idSchema, age: ageSchema }).strict(),
    z.object({ kind: z.literal('year'), year: yearSchema }).strict(),
    /** The projection's last year (a legacy "at the end", "for the rest of the plan"). */
    z.object({ kind: z.literal('endOfPlan') }).strict(),
]);
/**
 * WHICH figure an "amount at an age" goal is read against (owner decision 3):
 * total net worth, net worth after any unmet shortfall, one wrapper's total,
 * or one named asset. A named asset is measured from the engine's per-asset
 * closing balances, which exist only for holdings the engine tracks
 * individually (DC pensions, property, business interests, other assets and
 * Junior ISAs); a target naming a pooled holding is reported as outside the
 * projection with that reason, never measured against a figure the engine
 * cannot attribute.
 */
export const goalSeriesSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('netWorth') }).strict(),
    z.object({ kind: z.literal('netWorthAfterShortfall') }).strict(),
    z.object({ kind: z.literal('wrapper'), wrapper: wrapperSchema }).strict(),
    z.object({ kind: z.literal('asset'), assetId: idSchema }).strict(),
    /**
     * "Leave the children £X": the estate passed on after the estimated
     * inheritance tax at the plan's assessment year (the last modelled death,
     * else the horizon end) — the engine's `estimatedIht.passedOn`. Measured at
     * `endOfPlan` only.
     */
    z.object({ kind: z.literal('legacy') }).strict(),
]);
/** Where the goal came from, for the assumptions report (FEATURES §0.6). */
export const goalSourceSchema = z.enum(['client', 'preset', 'adviser']);
const goalBaseFields = {
    id: idSchema,
    /** The goal as the client stated it — the card title everywhere it renders. */
    label: labelSchema,
    source: goalSourceSchema.optional(),
};
/**
 * A client goal: a stated aim, MEASURED against the projection and never
 * optimised for. Outcomes are derived on every run by the engine's pure
 * evaluator and are never persisted — a goal record holds only what the
 * client said.
 *
 * `amountAtAge` — "have £X by age Y": the chosen series at the anchor year
 * against the stated figure, on the stated money basis (`today` deflates the
 * modelled figure with the run's own deflator; `future` compares nominal).
 *
 * `fundFromSurplus` — "put £X a year into the child's Junior ISA if there is
 * room": the record behind ONE `jisa` step in the surplus waterfall (linked by
 * the step's `goalId`). The yearly amount is in today's terms and escalates
 * like a contribution; the goal stops at `endAnchor`, or when the child turns
 * 18 if none is given. Catch-up of unfunded years is not modelled.
 */
export const householdGoalSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('amountAtAge'),
        ...goalBaseFields,
        anchor: goalAnchorSchema,
        target: nonNegativePenceSchema,
        terms: z.enum(['today', 'future']),
        series: goalSeriesSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal('fundFromSurplus'),
        ...goalBaseFields,
        /**
         * Where the surplus goes when there is room: a child's Junior ISA; extra
         * into a person's pension; a spend (the yearly holiday, regular support,
         * a one-off deposit gift); or an overpayment on a debt. Each is ONE step
         * in the surplus waterfall, linked by the step's `goalId`.
         */
        target: z.enum(['jisa', 'pension', 'spend', 'liability']),
        /** `jisa`: the child. */
        dependentId: idSchema.optional(),
        /** `jisa`: the dependent's Junior ISA asset the surplus is placed into. */
        assetId: idSchema.optional(),
        /** `pension`: whose pension; absent = whoever is alive, in plan order. */
        personId: personIdSchema.optional(),
        /** `liability`: the debt an overpayment goes to. */
        liabilityId: idSchema.optional(),
        /** `spend`: a gift rather than consumption — it joins the inheritance-tax gift list. */
        isGift: z.boolean().optional(),
        /** `spend`: paid once, in the start anchor's year, rather than every year. */
        oneOff: z.boolean().optional(),
        /**
         * `spend`: what the money is for, as a short noun the client-facing
         * sentence can carry ("the yearly holiday", "help with a deposit") —
         * the full `label` is the card title, not a phrase mid-sentence.
         */
        purpose: z.string().trim().min(1).max(80).optional(),
        /** Yearly (or one-off) amount, today's terms. */
        amount: nonNegativePenceSchema,
        escalation: escalationSchema.optional(),
        /** First active year; absent = the plan start. */
        startAnchor: goalAnchorSchema.optional(),
        /** Last active year; absent = the plan end (a `jisa` goal: the child's 18th). */
        endAnchor: goalAnchorSchema.optional(),
    })
        .strict(),
    /**
     * "Sustain £X a year from age A to B" (mechanic c): every year in the window
     * funds its spending (no unmet shortfall) and, when a figure is stated, the
     * household's income on the stated basis reaches it. `basis: 'net'` is
     * income after tax; `'gross'` before. An import that could not tell sets
     * `basisAssumed` so the adviser's card says the basis was assumed and can
     * be switched. Omit `amount` for "spending funded through these years"
     * (school-fee years).
     */
    z
        .object({
        kind: z.literal('spendingWindow'),
        ...goalBaseFields,
        amount: nonNegativePenceSchema.optional(),
        basis: z.enum(['gross', 'net']),
        basisAssumed: z.boolean().optional(),
        terms: z.enum(['today', 'future']),
        fromAnchor: goalAnchorSchema,
        toAnchor: goalAnchorSchema,
    })
        .strict(),
    /** "Mortgage-free by 55" (mechanic d): the debt's payoff year against the anchor. */
    z
        .object({
        kind: z.literal('liabilityClearBy'),
        ...goalBaseFields,
        liabilityId: idSchema,
        anchor: goalAnchorSchema,
    })
        .strict(),
]);
/** The cross-field rules a discriminated union cannot carry on its members. */
export const householdGoalRefinedSchema = householdGoalSchema.superRefine((g, ctx) => {
    if (g.kind !== 'fundFromSurplus')
        return;
    if (g.target === 'jisa' && (g.dependentId === undefined || g.assetId === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dependentId'],
            message: 'A Junior ISA goal must name the child and their Junior ISA',
        });
    }
    if (g.target === 'liability' && g.liabilityId === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['liabilityId'],
            message: 'An overpayment goal must name the debt',
        });
    }
});
/** Owner decision 10: at most twenty goals on a document. */
export const MAX_GOALS_PER_DOCUMENT = 20;
/**
 * A fund-if-surplus goal and its sweep step travel together (client goals):
 * every such goal has exactly ONE step carrying its `goalId`, and every
 * `goalId` on a step names a goal on the document. `withGoalStepsSynced` is
 * how every writer keeps that true; this refinement is the fail-closed check
 * for a writer that did not — a stale bundle or a non-browser client — so a
 * goal can never be persisted with no step, and money is never placed under a
 * deleted goal's name (tech-admin review, 2026-09-04).
 */
function refineGoalSteps(doc, ctx) {
    const goals = doc.goals ?? [];
    const steps = doc.waterfall?.steps ?? [];
    const goalIds = new Set(goals.map((g) => g.id));
    goals.forEach((g, i) => {
        if (g.kind !== 'fundFromSurplus')
            return;
        const n = steps.filter((s) => s.goalId === g.id).length;
        if (n !== 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['goals', i],
                message: n === 0
                    ? 'This goal places surplus but has no step in the surplus order — add it again from the Goals section.'
                    : 'This goal has more than one step in the surplus order.',
            });
        }
    });
    steps.forEach((s, j) => {
        if (s.goalId !== undefined && !goalIds.has(s.goalId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['waterfall', 'steps', j, 'goalId'],
                message: 'This surplus step belongs to a goal that is no longer on the plan.',
            });
        }
    });
}
/** The full household projection request — every Stage 4 field expressible. */
const householdProjectionRequestObject = z
    .object({
    clientId: clientIdSchema,
    asOfDate: isoDateSchema,
    startYear: yearSchema,
    people: z.array(householdPersonSchema).min(1).max(2),
    dependents: z.array(householdDependentSchema).max(20).optional(),
    incomeStreams: z.array(householdIncomeStreamSchema).max(100),
    assets: z.array(householdAssetSchema).max(200),
    liabilities: z.array(householdLiabilitySchema).max(100),
    spending: z.array(householdSpendingItemSchema).max(200),
    /**
     * FG-F3-04. What the household's JOINT spending becomes once one of them
     * has died, in bps of the joint figure — the adviser's stated position, on
     * the household rather than on each row.
     *
     * Absent keeps today's behaviour exactly (the full joint figure carries on)
     * AND raises `survivor-spending-unmodelled`. Never a default that silently
     * changes the answer: "one person doesn't run two cars and two phones and
     * eat for two", but nor does the tool know what they do run.
     */
    survivorSpendingFactorBps: bpsSchema.min(0).max(10_000).optional(),
    lifeEvents: z.array(householdLifeEventSchema).max(300).optional(),
    /** deposit ≤ price is re-checked by the engine (fail-closed twice). */
    plannedEvents: z.array(plannedEventSchema).max(100).optional(),
    /** Year-anchored stress inputs; shocks in the same year stack. */
    marketShocks: z.array(marketShockSchema).max(20).optional(),
    assumptions: assumptionSpecSchema,
    window: projectionWindowSchema.optional(),
    waterfall: surplusWaterfallSchema.optional(),
    withdrawalWaterfall: withdrawalWaterfallSchema.optional(),
    iht: ihtSettingsSchema.optional(),
    giaCgtMode: z.enum(['onWithdrawal', 'annually']).optional(),
    /**
     * Client goals (2026-09-03): stated aims measured against the run. Optional
     * and additive so every document and every stored `inputs_snapshot` written
     * before goals existed still parses. The engine never reads these to change
     * a figure; `evaluateGoals` reads them after the run.
     */
    goals: z.array(householdGoalRefinedSchema).max(MAX_GOALS_PER_DOCUMENT).optional(),
    /**
     * B2.1 — arrangements the plan RECORDS and does not model. Optional and
     * additive, so every document and every stored `inputs_snapshot` written
     * before it still parses.
     *
     * A trust is the commonest thing an adviser has to leave out of a
     * cash-flow, and leaving it out silently is the failure: the plan looks
     * complete while the largest arrangement in the client's affairs is nowhere
     * in it. Recorded here it is on the file, named on every client surface,
     * and stated as not calculated.
     *
     * **No value field in v1** (owner decision N-B2). A figure would invite the
     * reader to add it to the estate, which is the one thing this tool must not
     * do with an arrangement it does not assess.
     */
    trusts: z.array(householdTrustSchema).max(20).optional(),
    /**
     * Stage 7: include the full per-year calculation trace in the preview
     * response. Default false — the preview is the hot live-editing path and
     * the UI usually only draws the chart; the explain endpoint serves the
     * drill-down.
     */
    includeTrace: z.boolean().optional(),
})
    .strict();
export const householdProjectionRequestSchema = householdProjectionRequestObject.superRefine((req, ctx) => {
    refineSingleMainResidence(req, ctx);
    refineGoalSteps(req, ctx);
});
/**
 * The same request shape WITHOUT the single-main-residence cross-check, for
 * reading back data that was written before the rule existed — specifically an
 * immutable `household_projection_runs.inputs_snapshot`. Every other rule
 * (pence, bounds, strictness) still applies. Never use this to validate an
 * incoming request: writes go through `householdProjectionRequestSchema`.
 */
/**
 * One published firm assumption version, resolved to the figures the engine
 * reads. Server-produced only; never accepted from a live request.
 */
export const pinnedAssumptionSetSchema = z
    .object({
    setId: z.string().uuid(),
    versionId: z.string().uuid(),
    versionNumber: z.number().int().min(1),
    name: z.string().min(1).max(80),
    contentHash: z.string().regex(/^[0-9a-f]{64}$/),
    bundle: firmAssumptionPresetSchema.omit({ displayLabel: true }).strict(),
    /**
     * The set's named indices (spec §5.1), resolved by the server to one flat
     * annual rate each — the figures an item's index-linked escalation reads.
     * Absent on a version that names none, and on runs stamped before indices
     * existed.
     */
    customIndices: z.record(indexNameSchema, bpsSchema.min(-10_000).max(10_000)).optional(),
    /** How each was arrived at, for the report's assumptions table. */
    indexBasis: z.record(indexNameSchema, z.string().max(200)).optional(),
})
    .strict();
export const householdProjectionRequestStoredSchema = householdProjectionRequestObject.extend({
    /**
     * Server-stamped at persist time (never accepted from a live request —
     * tenant config comes from the database, not the caller): the firm's own
     * assumption preset bundles in force when the run executed, so a replay
     * resolves against the SAME figures forever, whatever the firm changes
     * later. Absent on runs persisted before Firm settings existed, and for
     * firms running on the product defaults.
     */
    firmPresets: firmAssumptionPresetsSchema.optional(),
    /**
     * Server-stamped, like `firmPresets`: every published firm assumption
     * version a `setVersionId` in this request named, ALREADY resolved to its
     * figures. A replay reads these — never the database — so archiving the set,
     * publishing a later version or changing the firm default cannot restate a
     * stored run or a report built from it.
     */
    pinnedSets: z.array(pinnedAssumptionSetSchema).max(50).optional(),
});
// --- household fact-find document (Stage 9, the builder) ----------------------
/**
 * The DURABLE household fact-find document the builder edits and persists —
 * exactly the projection request minus its per-run fields (`clientId` lives in
 * the route path; `asOfDate`/`startYear` are injected at run time by the
 * caller's clock; `includeTrace` is a response-shaping flag). One live
 * document per client (migration 0014), re-validated against this schema on
 * every write so nothing non-contractual can ever be persisted.
 */
/**
 * Fact-find ENTRY metadata (adviser dashboard redesign, 2026-09-13 §7). The
 * collections an adviser has explicitly said hold nothing — "None to add" —
 * so an unanswered section and an answered-empty section are different
 * persisted states. UI-only: the engine never reads it, and
 * `projectionRequestFromDocument` strips it before a request is built, so it
 * can never reach an `inputs_snapshot`. Absent on every document written
 * before it existed, which the builder reads as "not started" — nothing old
 * is marked complete by default.
 */
export const FACT_FIND_ENTRY_SECTIONS = [
    'assets',
    'income',
    'spending',
    'liabilities',
    'futurePlans',
];
export const factFindEntrySectionSchema = z.enum(FACT_FIND_ENTRY_SECTIONS);
export const factFindEntrySchema = z
    .object({
    /** Sections the adviser recorded as "None to add". Order is not significant. */
    none: z.array(factFindEntrySectionSchema).max(FACT_FIND_ENTRY_SECTIONS.length).optional(),
})
    .strict();
const householdFactFindDocumentObject = householdProjectionRequestObject
    .omit({
    clientId: true,
    asOfDate: true,
    startYear: true,
    includeTrace: true,
})
    .extend({
    entry: factFindEntrySchema.optional(),
    /** Retained facts from meeting-note imports and the adviser's decisions on
     * them (`import-review.ts`). Document-only; stripped before projection. */
    importReview: importReviewSchema.optional(),
});
export const householdFactFindDocumentSchema = householdFactFindDocumentObject.superRefine((doc, ctx) => {
    refineSingleMainResidence(doc, ctx);
    refineGoalSteps(doc, ctx);
});
/**
 * The stored fact-find as it may be READ BACK: identical except that the
 * single-main-residence cross-check is not applied, so a document saved before
 * that rule can still be loaded and corrected in the builder rather than
 * locking the adviser out of the client. Writes still use the strict schema.
 */
export const householdFactFindDocumentStoredSchema = householdFactFindDocumentObject;
/**
 * Drop the UI-only `entry` key from a stored fact-find payload. The projection
 * request is `.strict()`, so a document spread straight into it would be
 * refused the moment an adviser recorded "None to add" — every place that
 * turns a document into a request goes through here or through
 * `projectionRequestFromDocument`.
 */
export function withoutFactFindEntry(payload) {
    // `importReview` is document-only too (import-review.ts): the retained facts
    // and review answers must never reach a projection request or its snapshot.
    const { entry: _entry, importReview: _importReview, ...rest } = payload;
    return rest;
}
/** The projection request for a fact-find document at the caller's clock. */
export function projectionRequestFromDocument(document, run) {
    const request = { ...withoutFactFindEntry(document), ...run };
    return request;
}
/** PUT body: the full document plus the optimistic-concurrency guard.
 * `expectedVersion` null = "creating the first document" (409 if one exists);
 * a number = "replacing exactly that version" (409 if it moved). */
export const householdFactFindPutSchema = z
    .object({
    document: householdFactFindDocumentSchema,
    expectedVersion: z.number().int().min(1).nullable(),
})
    .strict();
/** GET/PUT response: `factFind` is null when the client has no document yet
 * (a valid, expected state for a new client — distinct from 404 wrong-tenant). */
export const householdFactFindReadSchema = z.object({
    clientId: clientIdSchema,
    factFind: z
        .object({
        // Reading back is lenient about the single-main-residence rule ONLY —
        // see the note on `refineSingleMainResidence`.
        document: householdFactFindDocumentStoredSchema,
        version: z.number().int().min(1),
        updatedAt: z.string().datetime(),
    })
        .nullable(),
});
// --- scenarios (Stage 6) ------------------------------------------------------
/**
 * What-if tax-rule changes for ONE scenario (feedback 2026-08-12: "adjust
 * potential tax rule changes … in a very simple way at face value and if
 * wanted an advanced settings to change more details").
 *
 * Each field patches the named figure in the versioned rule set for THIS
 * scenario's run only; the patched set is stamped `<version>+what-if` so no
 * run can claim the official rule-set identity for modified rules (see
 * @cf/tax applyTaxOverrides). These are hypotheses an adviser wants to
 * stress-test, never a statement of law — the UI says so.
 *
 * `incomeTaxRateDeltaBps` is the simple face-value lever: every income-tax
 * rate (basic/higher/additional — not dividends) shifted together, e.g. +200
 * = "2p in the pound more". The rest are the advanced absolutes.
 */
export const scenarioTaxOverridesSchema = z
    .object({
    /** Simple lever: shift basic+higher+additional rates together (±10p). */
    incomeTaxRateDeltaBps: bpsSchema.min(-1000).max(1000).optional(),
    personalAllowance: nonNegativePenceSchema.optional(),
    basicRateBps: bpsSchema.min(0).max(10_000).optional(),
    higherRateBps: bpsSchema.min(0).max(10_000).optional(),
    additionalRateBps: bpsSchema.min(0).max(10_000).optional(),
    /** Top of the basic-rate band, in taxable income (after the allowance). */
    basicBandTop: nonNegativePenceSchema.optional(),
    additionalRateThreshold: nonNegativePenceSchema.optional(),
    dividendAllowance: nonNegativePenceSchema.optional(),
    cgtAnnualExemptAmount: nonNegativePenceSchema.optional(),
    cgtLowerRateBps: bpsSchema.min(0).max(10_000).optional(),
    cgtHigherRateBps: bpsSchema.min(0).max(10_000).optional(),
    isaAnnualSubscriptionLimit: nonNegativePenceSchema.optional(),
    pensionAnnualAllowance: nonNegativePenceSchema.optional(),
    ihtNilRateBand: nonNegativePenceSchema.optional(),
    ihtResidenceNilRateBand: nonNegativePenceSchema.optional(),
    ihtStandardRateBps: bpsSchema.min(0).max(10_000).optional(),
})
    .strict();
/**
 * A named overlay of overrides applied to the base household at run time —
 * deltas only, resolved by the engine's `resolveScenario`, never a copy of
 * the client data.
 */
export const scenarioOverlaySchema = z
    .object({
    id: idSchema,
    label: labelSchema,
    /** Re-resolve the assumption set (preset/portfolio/overrides). */
    assumptions: assumptionSpecSchema.optional(),
    /**
     * DELTA over the base's resolved assumptions ("same as base but…") —
     * mutually exclusive with `assumptions`.
     */
    assumptionOverrides: globalOverridesSchema.optional(),
    retirementAges: z
        .object({ client: ageSchema.optional(), partner: ageSchema.optional() })
        .strict()
        .optional(),
    deathAges: z
        .object({
        client: ageSchema.nullable().optional(),
        partner: ageSchema.nullable().optional(),
    })
        .strict()
        .optional(),
    addLifeEvents: z.array(householdLifeEventSchema).max(50).optional(),
    removeLifeEventIds: z.array(idSchema).max(50).optional(),
    addPlannedEvents: z.array(plannedEventSchema).max(50).optional(),
    removePlannedEventIds: z.array(idSchema).max(50).optional(),
    addMarketShocks: z.array(marketShockSchema).max(20).optional(),
    removeMarketShockIds: z.array(idSchema).max(20).optional(),
    window: projectionWindowSchema.optional(),
    waterfall: surplusWaterfallSchema.optional(),
    withdrawalWaterfall: withdrawalWaterfallSchema.optional(),
    iht: ihtSettingsSchema.optional(),
    giaCgtMode: z.enum(['onWithdrawal', 'annually']).optional(),
    /** What-if tax-rule changes for this scenario's run only. */
    taxOverrides: scenarioTaxOverridesSchema.optional(),
})
    .strict();
/**
 * Compare request: the base household plus 1–4 named scenario overlays.
 * Overlays in a COMPARISON may not change the projection window/horizon —
 * the per-year series must stay aligned or the headline numbers compare
 * different spans of time.
 */
export const householdProjectionCompareRequestSchema = householdProjectionRequestObject
    .extend({
    scenarios: z.array(scenarioOverlaySchema).min(1).max(4),
})
    .superRefine((req, ctx) => {
    refineSingleMainResidence(req, ctx);
    req.scenarios.forEach((s, i) => {
        if (s.window !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['scenarios', i, 'window'],
                message: 'A compared scenario cannot change the projection window (series must align)',
            });
        }
        if (s.assumptions?.horizonYears !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['scenarios', i, 'assumptions', 'horizonYears'],
                message: 'A compared scenario cannot change the horizon (series must align)',
            });
        }
        if (s.assumptions !== undefined && s.assumptionOverrides !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['scenarios', i, 'assumptionOverrides'],
                message: 'Give assumptions (replace the bundle) OR assumptionOverrides (delta), not both',
            });
        }
    });
});
// --- saved scenario definitions (the Cash Flow Modeller's durable what-ifs) ---------
/**
 * The DURABLE what-if definition stored on a `scenarios` row: exactly a
 * scenario overlay minus its per-run identity (`id` is the row id, `label` is
 * the row name). Window/horizon changes are refused so every saved definition
 * stays comparable against the base (aligned per-year series), matching the
 * compare-request rule.
 */
/**
 * Per-scenario sparse override of the fact-find collections the overlay
 * vocabulary cannot reach (decision: docs/product/scenario-model.md). For a
 * collection: `upsert` replaces-by-id when the id exists in the base document
 * and appends otherwise; `removeIds` switches items off for this scenario
 * only. The patch is applied to the DOCUMENT before the engine mapping, and
 * the patched document is re-validated against the full fact-find contract —
 * the Engine contract is untouched. Base-plan edits keep flowing into every
 * scenario except for the specific items a scenario pins.
 */
const collectionPatchSchema = (item, max) => z
    .object({
    upsert: z.array(item).max(max).optional(),
    removeIds: z.array(idSchema).max(max).optional(),
})
    .strict()
    .optional();
export const scenarioDocumentPatchSchema = z
    .object({
    people: collectionPatchSchema(householdPersonSchema, 2),
    dependents: collectionPatchSchema(householdDependentSchema, 20),
    incomeStreams: collectionPatchSchema(householdIncomeStreamSchema, 100),
    assets: collectionPatchSchema(householdAssetSchema, 200),
    liabilities: collectionPatchSchema(householdLiabilitySchema, 100),
    spending: collectionPatchSchema(householdSpendingItemSchema, 200),
    /** Scenario-level goal edits (v2): "what if we drop the Junior ISA goal". */
    goals: collectionPatchSchema(householdGoalRefinedSchema, MAX_GOALS_PER_DOCUMENT),
    /**
     * A2.5a review round 2 (adviser). `deathAge` is patchable through `people`,
     * so "duplicate the plan, model him dying first, compare" was reachable —
     * and the household-level survivor factor was not, so the one scenario the
     * whole survivor gate exists for could model the death and had no way to
     * state the survivor's spending. The scenario then tripped
     * `survivor-spending-unmodelled` with no control anywhere that could
     * answer it. Household-level, so a scalar rather than a collection patch:
     * present replaces, absent inherits the base document's value.
     */
    survivorSpendingFactorBps: bpsSchema.min(0).max(10_000).optional(),
})
    .strict();
export const scenarioDefinitionSchema = scenarioOverlaySchema
    .omit({ id: true, label: true })
    .extend({ documentPatch: scenarioDocumentPatchSchema.optional() })
    .superRefine((d, ctx) => {
    if (d.window !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['window'],
            message: 'A saved scenario cannot change the projection window (series must align)',
        });
    }
    if (d.assumptions?.horizonYears !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['assumptions', 'horizonYears'],
            message: 'A saved scenario cannot change the horizon (series must align)',
        });
    }
    if (d.assumptions !== undefined && d.assumptionOverrides !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['assumptionOverrides'],
            message: 'Give assumptions (replace the bundle) OR assumptionOverrides (delta), not both',
        });
    }
});
/**
 * A firm template's `name` becomes the scenario label on a client's chart and
 * in the PDF, and `source` is printed beside it. Both are free text typed by
 * one owner and inherited by every client of the firm, so they take the same
 * wording guard C1.4 already applies to the preset register: this is a
 * modelling aid, and a label reading "Prudent test" or "Recommended view" is a
 * verdict on the plan, not a description of what varies.
 *
 * `PRESET_BANNED_WORDS` is deliberately reused rather than restated — a second
 * list is a second thing to keep in step, and the register's list was written
 * for exactly this failure.
 */
const firmTemplateWordingSchema = (max) => z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((v) => !PRESET_BANNED_WORDS.test(v), {
    message: 'Name what varies (e.g. "Markets fall 30% in year 1, recovering over 4 years"), not a verdict on the plan',
});
/**
 * GOAL 2026-09-05 C2.2 — a FIRM scenario template: the firm's standard stress
 * set and its house scenario shapes, in one shape.
 *
 * `definition` is a firm-level SHAPE and nothing else. A template is firm data,
 * shared across every client of the firm, so a client's figures stored here
 * would be readable by advisers who have no access to that client.
 *
 * It is therefore a `.pick()` of named keys rather than an `.omit()` of the
 * ones that looked dangerous. The first cut omitted two keys and let six
 * through, and the review that followed found client data riding in two MORE
 * keys that had looked innocent: `waterfall` steps carry `dependentId`,
 * `goalId`, `personId` and a label in the client's own words, and `iht` carries
 * `residenceToDescendants` and `priorYearExemptionUsed` — one client's property
 * value and gifting history. Migration 0036's check constraint inspects
 * TOP-LEVEL keys only and structurally cannot see either. An allowlist can.
 *
 * The cost is real and is recorded rather than hidden: a firm cannot define a
 * house waterfall or a house IHT position here. Re-admitting them means a
 * firm-scoped variant of those schemas with the client references removed, not
 * relaxing this list.
 */
export const firmScenarioTemplateDefinitionSchema = scenarioOverlaySchema
    .pick({
    assumptions: true,
    assumptionOverrides: true,
    retirementAges: true,
    deathAges: true,
    /**
     * The reason cluster C exists. A market shock is `year`/`dropBpsByWrapper`
     * or `anchor`/`path` — a SHAPE, with no client figure in it, so "markets
     * fall 30% in year one, anchored to the year after retirement" is exactly
     * the firm-level object this table is for.
     */
    addMarketShocks: true,
    giaCgtMode: true,
    taxOverrides: true,
})
    .strict()
    .superRefine((d, ctx) => {
    /**
     * A template BECOMES a scenario, so anything `scenarioDefinitionSchema`
     * refuses has to be refused here too — where the owner is standing. All
     * three of these saved 200 and were then refused at the moment an adviser
     * ran them against a client's plan, weeks later.
     */
    if (d.assumptions?.horizonYears !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['assumptions', 'horizonYears'],
            message: 'A saved scenario cannot change the horizon (series must align)',
        });
    }
    if (d.assumptions !== undefined && d.assumptionOverrides !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['assumptionOverrides'],
            message: 'Give assumptions (replace the bundle) OR assumptionOverrides (delta), not both',
        });
    }
    // A named "Severe downturn" that changes nothing draws two identical lines
    // on a client's chart while the adviser explains a downturn.
    if (Object.keys(d).length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A template has to change something',
        });
    }
});
export const firmScenarioTemplateSchema = z
    .object({
    id: idSchema,
    name: firmTemplateWordingSchema(120),
    /**
     * `stress` is the firm's standard adverse set; `shape` is a house scenario
     * an adviser applies as a starting point. One table, because they are the
     * same object with different intent — see 0036.
     */
    kind: z.enum(['stress', 'shape']),
    definition: firmScenarioTemplateDefinitionSchema,
    /** Where the shape came from, so a report can say it (C1.4's discipline). */
    source: firmTemplateWordingSchema(200).optional(),
    /**
     * The NAME of the human who signed the shape off, as C1.4's preset register
     * records it — not an adviser id. The first cut made this a uuid FK to
     * `advisers`, which put a cross-tenant coupling on a provenance field: FK
     * checks bypass RLS, so one firm's template could name another firm's
     * adviser, and `on delete restrict` then let firm A's template block the
     * erasure of firm B's adviser row. No other migration in the repo carries a
     * `reviewed_by` uuid FK; C1.4 stores a name, and so does this.
     */
    reviewedBy: z.string().trim().min(3).max(120).optional(),
    reviewedAt: z.string().datetime().optional(),
})
    .strict()
    .superRefine((t, ctx) => {
    // A half-recorded review is worse than none: it reads as signed off while
    // naming nobody, or names somebody with no date to hold them to.
    if ((t.reviewedBy === undefined) !== (t.reviewedAt === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A reviewed template needs both who reviewed it and when',
            path: [t.reviewedBy === undefined ? 'reviewedBy' : 'reviewedAt'],
        });
    }
});
/** The firm's whole library, as the settings page reads it. */
export const firmScenarioTemplatesResponseSchema = z
    .object({ templates: z.array(firmScenarioTemplateSchema).max(40) })
    .strict();
/**
 * The PUT body is the WHOLE library: a template the body omits has been
 * retired, and is soft-deleted rather than removed — the same contract shape
 * `firm/assumptions` uses, so the two firm-settings surfaces behave alike.
 */
const firmScenarioTemplateDraftSchema = firmScenarioTemplateSchema
    .innerType()
    /**
     * No `id`. The route matches by NAME, and accepting an id it never reads
     * would let a settings page send "rename this row" and get retire-plus-create
     * instead — the one operation that orphans a retired row's provenance. The
     * contract should not imply an identity the route ignores.
     */
    .omit({ id: true })
    .superRefine((t, ctx) => {
    /**
     * The review rule has to be RE-APPLIED here. `.innerType()` unwraps the
     * ZodEffects to reach `.omit()`, and unwrapping drops every cross-field
     * rule with it — so the update schema silently accepted a half-recorded
     * review that the read schema refuses, and the route then failed at the
     * database instead of returning a field-level 422. Found by the route's own
     * fail-closed matrix, which is exactly what that half of the matrix is for.
     */
    if ((t.reviewedBy === undefined) !== (t.reviewedAt === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A reviewed template needs both who reviewed it and when',
            path: [t.reviewedBy === undefined ? 'reviewedBy' : 'reviewedAt'],
        });
    }
});
export const firmScenarioTemplatesUpdateSchema = z
    .object({ templates: z.array(firmScenarioTemplateDraftSchema).max(40) })
    .strict()
    .superRefine((body, ctx) => {
    /**
     * The library is keyed by name, and the insert loop resolves a second
     * template of the same name onto the first: six go in, five come back, 200,
     * no warning. Refused here because the loss is silent.
     */
    const seen = new Set();
    body.templates.forEach((t, i) => {
        if (seen.has(t.name)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['templates', i, 'name'],
                message: 'Two templates cannot share a name',
            });
        }
        seen.add(t.name);
    });
});
/** PATCH body for a scenario: any of name/description/definition, at least one. */
export const scenarioUpdateSchema = z
    .object({
    expectedUpdatedAt: z.string().datetime(),
    name: z.string().trim().min(1, 'A scenario name is required').max(120).optional(),
    description: z.string().max(1000).nullable().optional(),
    /** `null` clears a saved definition. */
    definition: scenarioDefinitionSchema.nullable().optional(),
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.name === undefined &&
        body.description === undefined &&
        body.definition === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Provide at least one field to update',
        });
    }
});
/** Read shape for scenario detail: the row plus its saved definition (if any). */
export const scenarioDetailReadSchema = scenarioReadSchema.extend({
    definition: scenarioDefinitionSchema.nullable(),
});
// --- response ---------------------------------------------------------------
const auditRowSchema = z.object({
    openingAssets: nonNegativePenceSchema,
    income: nonNegativePenceSchema,
    pensionReliefTopUp: nonNegativePenceSchema,
    employerContributions: nonNegativePenceSchema,
    lifeEventInflows: nonNegativePenceSchema,
    spending: nonNegativePenceSchema,
    debtService: nonNegativePenceSchema,
    lifeEventOutflows: nonNegativePenceSchema,
    tax: nonNegativePenceSchema,
    /**
     * Student loan repayments leaving the household this year. Its own audit line
     * because it is a real outflow that is NOT tax — folding it into `tax` would
     * make the conservation identity balance while quietly overstating the
     * client's tax bill. OPTIONAL and OMITTED when nil, so a run with no loan
     * serialises exactly as it did before this field existed.
     */
    studentLoanRepayment: nonNegativePenceSchema.optional(),
    fees: nonNegativePenceSchema,
    growth: z.number().int(),
    unmetShortfall: nonNegativePenceSchema,
    closingAssets: nonNegativePenceSchema,
    openingDebt: nonNegativePenceSchema,
    debtDrawn: nonNegativePenceSchema,
    debtInterest: nonNegativePenceSchema,
    debtRepaid: nonNegativePenceSchema,
    closingDebt: nonNegativePenceSchema,
});
const personYearTaxSchema = z.object({
    personId: personIdSchema,
    grossIncome: nonNegativePenceSchema,
    incomeTax: nonNegativePenceSchema,
    nationalInsurance: nonNegativePenceSchema,
    capitalGainsTax: nonNegativePenceSchema,
    section24Reduction: nonNegativePenceSchema,
    totalTax: nonNegativePenceSchema,
    /**
     * This person's student loan repayment for the year. It rides on the tax row
     * because PAYE collects it alongside tax and an adviser reads the two
     * together — but it is a DEBT REPAYMENT and is deliberately NOT part of
     * `totalTax`. Adding it there would make the tool disagree with the client's
     * P60. OPTIONAL and OMITTED when nil.
     */
    studentLoanRepayment: nonNegativePenceSchema.optional(),
});
/** Why a sweep step placed less than it could have, if it did. */
export const sweepStepLimitSchema = z.enum([
    'none',
    'noSurplus',
    'earlierSteps',
    'statutoryLimit',
    'annualCap',
    'childOver18',
    'funderDeceased',
    'noRoom',
    'inactive',
    /** `liability`: the debt was already cleared. */
    'debtCleared',
    /** `pension` goal steps: the person is past their retirement age (or 75), so a pension cannot take more. */
    'retired',
]);
export const sweepStepLineSchema = z.object({
    target: surplusSweepTargetSchema,
    dependentId: idSchema.optional(),
    goalId: idSchema.optional(),
    personId: personIdSchema.optional(),
    liabilityId: idSchema.optional(),
    /** What the step asked for this year (its cap, escalated); null = uncapped. */
    desired: nonNegativePenceSchema.nullable(),
    placed: nonNegativePenceSchema,
    limit: sweepStepLimitSchema,
});
const balancesByWrapperSchema = z.object(Object.fromEntries(Object.values(Wrapper).map((w) => [w, nonNegativePenceSchema])));
export const householdProjectionYearSchema = z.object({
    year: yearSchema,
    taxYearLabel: z.string(),
    clientAge: ageSchema,
    balancesByWrapper: balancesByWrapperSchema,
    liabilitiesOutstanding: nonNegativePenceSchema,
    totalNetWorth: z.number().int(),
    grossIncome: nonNegativePenceSchema,
    spending: nonNegativePenceSchema,
    debtService: nonNegativePenceSchema,
    tax: z.array(personYearTaxSchema),
    totalTax: nonNegativePenceSchema,
    /**
     * The household's total student loan repayment for the year — the sum of
     * every person's loans. Its own field, NOT part of `totalTax`, and it is
     * already deducted from `netCashFlow` below.
     *
     * OPTIONAL and OMITTED when nil: a projection with no loan therefore
     * serialises byte-identically to one run before this field existed, which is
     * what keeps the golden masters and every stored run stable.
     */
    studentLoanRepayment: nonNegativePenceSchema.optional(),
    netCashFlow: z.number().int(),
    unmetShortfall: nonNegativePenceSchema,
    /**
     * Running total of every unmet shortfall up to and including this year.
     * Optional on the wire: runs stored under engine < 3.1.0 predate it.
     */
    cumulativeUnmetShortfall: nonNegativePenceSchema.optional(),
    /**
     * `totalNetWorth − cumulativeUnmetShortfall`: the honest headline once the
     * plan has stopped being able to fund its own spending. Optional on the
     * wire for pre-3.1.0 stored runs.
     */
    netWorthAfterShortfall: z.number().int().optional(),
    /**
     * Cumulative price deflator, integer parts-per-million (1_000_000 in the
     * start year). Deflate any pence figure with `toTodaysPence`. Optional on
     * the wire for pre-3.1.0 stored runs.
     */
    deflatorPpm: z.number().int().positive().optional(),
    /**
     * Closing balance per individually-tracked asset (DC pensions, property,
     * business interests, other assets, Junior ISAs), keyed by asset id — the
     * series a named-asset goal is measured against. Additive (client goals,
     * 2026-09-03); OMITTED when the plan holds no such asset, and absent on runs
     * stored before it existed.
     */
    balancesByAsset: z
        .record(idSchema, nonNegativePenceSchema)
        // CANONICAL key order on parse. Postgres jsonb re-sorts object keys (by
        // length, then bytes) while the engine emits them in plain string order,
        // so a stored run and its replay would serialise the same VALUES in two
        // orders and the "prove this number" byte comparison would fail (CI run
        // 33830157955, 2026-09-04). Both sides go through this schema, so both
        // come out in one order.
        .transform((record) => Object.fromEntries(Object.entries(record).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))))
        .optional(),
    /**
     * Closing balance per liability that has started, keyed by liability id
     * (canonical key order, as above) — the series a "mortgage clear by" goal is
     * measured against. Additive (client goals v1.5); OMITTED when the plan has
     * no started liability.
     */
    balancesByLiability: z
        .record(idSchema, nonNegativePenceSchema)
        .transform((record) => Object.fromEntries(Object.entries(record).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))))
        .optional(),
    /**
     * The surplus sweep's own ledger for the year, one line per configured step
     * in order: what the step could take (`desired`, null = uncapped), what it
     * took (`placed`), and what stopped it short. Additive; OMITTED when the plan
     * runs the neutral no-sweep default, and absent on runs stored before it
     * existed. A fund-if-surplus goal's card reads THIS, never a reconstruction.
     */
    sweepSteps: z.array(sweepStepLineSchema).optional(),
    audit: auditRowSchema,
});
const assumptionsSnapshotSchema = z.object({
    preset: z.enum(['conservative', 'moderate', 'optimistic', 'custom']),
    reviewPending: z.boolean(),
    entries: z.array(z.object({
        key: z.string(),
        valueBps: z.number().int(),
        source: z.enum(['default', 'preset', 'adviser-override', 'portfolio']),
        detail: z.string().nullable(),
    })),
    /** The exact firm version the base figures came from, when the plan pins one. */
    set: z
        .object({
        setId: z.string().uuid(),
        versionId: z.string().uuid(),
        versionNumber: z.number().int().min(1),
        name: z.string(),
        contentHash: z.string().regex(/^[0-9a-f]{64}$/),
    })
        .optional(),
});
/** One gift as an inheritance-tax assessment treated it (A1.3; shared by IE-2's per-year working). */
const ihtGiftReadoutSchema = z.object({
    year: yearSchema,
    label: z.string(),
    value: nonNegativePenceSchema,
    exemptionApplied: nonNegativePenceSchema,
    // A2.1, additive: the marriage exemption and the named recipient.
    weddingExemption: nonNegativePenceSchema.optional(),
    recipient: labelSchema.optional(),
    yearsSurvivedAtAssessment: z.number().int().min(0),
    taperBps: bpsSchema.min(0).max(10_000),
    nilRateBandUsed: nonNegativePenceSchema,
    outsideWindow: z.boolean(),
    /**
     * A2.2, additive and optional so a run stored before this build still
     * parses: the adviser's asserted normal-expenditure position, which took
     * the gift out of the seven-year clock entirely. Absent reads as false.
     */
    normalExpenditureAsserted: z.boolean().optional(),
    taxAtAssessment: nonNegativePenceSchema,
});
const ihtWrapperLineSchema = z.object({
    wrapper: wrapperSchema,
    /** SIGNED — see the composition note below. */
    value: z.number().int().safe(),
    inEstate: z.boolean(),
    excludedBecause: z
        .enum(['pensionsOutsideEstateByRule', 'pensionsOutsideEstateBySetting', 'childrensJisa'])
        .optional(),
    /**
     * The plan's asset records held in this wrapper, for a source link. An array
     * rather than a record so a stored run's key order cannot drift in jsonb.
     */
    assetIds: z.array(idSchema).readonly(),
});
/**
 * GOAL 2026-09-16 IE-2 — the whole working of ONE year's assessment, computed
 * by the engine so nothing on screen or in a report reconstructs tax logic.
 * Every amount is nominal integer pence; a today's-money view deflates each
 * amount AND each threshold with the same year's deflator.
 *
 * Identities the engine asserts before emitting (and the tests pin):
 *   householdNetWorth − pensionsExcluded − jisaExcluded = netEstate
 *   grossIncludedAssets − liabilities = netEstate
 *   netEstate − businessRelief + flooringAdjustment = estateValue
 *   estateExempt + estateBeforeBands = estateValue
 *   residenceUsed + nilRateBandUsed + taxableEstate = estateBeforeBands
 *   estateTax + giftTax = totalTax
 */
const ihtYearWorkingSchema = z.object({
    year: yearSchema,
    /** Whose holdings are assessed — never a person's estate the model cannot attribute. */
    basis: z.enum(['individual', 'lastSurvivor', 'householdAtDeath', 'householdAtHorizon']),
    /** The date convention: the estate at the END of this projection year. */
    taxYearLabel: z.string(),
    /** Net worth before any unfunded shortfall — the chart's line plus `unfundedShortfall`. */
    householdNetWorth: z.number().int().safe(),
    unfundedShortfall: nonNegativePenceSchema,
    pensionsExcluded: nonNegativePenceSchema,
    jisaExcluded: nonNegativePenceSchema,
    composition: z.array(ihtWrapperLineSchema).readonly(),
    grossIncludedAssets: z.number().int().safe(),
    liabilities: nonNegativePenceSchema,
    /** Signed: an estate can owe more than it holds. */
    netEstate: z.number().int().safe(),
    businessRelief: nonNegativePenceSchema,
    flooringAdjustment: z.number().int().safe(),
    /** Assets after reliefs and debts — the estate the bands and exemptions meet. */
    estateValue: nonNegativePenceSchema,
    /** After debts, before exemptions and reliefs: what the residence taper is tested on. */
    estateValueForTaper: nonNegativePenceSchema,
    estateExempt: nonNegativePenceSchema,
    estateBeforeBands: nonNegativePenceSchema,
    nilRateBand: z.object({
        own: nonNegativePenceSchema,
        transferredBps: bpsSchema.min(0).max(10_000),
        transferred: nonNegativePenceSchema,
        total: nonNegativePenceSchema,
        usedByGifts: nonNegativePenceSchema,
        remainingAfterGifts: nonNegativePenceSchema,
        usedByEstate: nonNegativePenceSchema,
    }),
    residenceNilRateBand: z.object({
        own: nonNegativePenceSchema,
        transferredBps: bpsSchema.min(0).max(10_000),
        transferred: nonNegativePenceSchema,
        maximum: nonNegativePenceSchema,
        taperReduction: nonNegativePenceSchema,
        afterTaper: nonNegativePenceSchema,
        residenceValue: nonNegativePenceSchema,
        available: nonNegativePenceSchema,
        usedByEstate: nonNegativePenceSchema,
        /** Where `residenceValue` came from. */
        source: z.enum(['notEntered', 'doesNotApply', 'fixedAmount', 'recordedMainResidence']),
        /** The main residence read, when `source` is `recordedMainResidence` and one is recorded. */
        residenceAssetId: idSchema.optional(),
    }),
    taxableEstate: nonNegativePenceSchema,
    rateBps: bpsSchema.min(0).max(10_000),
    rateBasis: z.enum(['standard', 'reducedRateAsserted']),
    estateTax: nonNegativePenceSchema,
    giftTax: nonNegativePenceSchema,
    totalTax: nonNegativePenceSchema,
    pensionTreatment: z.enum([
        'insideByRule',
        'outsideByRule',
        'insideBySetting',
        'outsideBySetting',
    ]),
    spouseExemptionApplied: z.boolean(),
    /**
     * `estimated` — every figure the working needs is recorded or ruled.
     * `incomplete` — the figure is computed, but on a stated gap (see `missing`).
     */
    status: z.enum(['estimated', 'incomplete']),
    missing: z.array(z.enum(['residenceNotEntered', 'mainResidenceNotRecorded'])).readonly(),
    gifts: z.array(ihtGiftReadoutSchema).readonly(),
});
const estimatedIhtSchema = z.object({
    year: yearSchema,
    atHorizonEnd: z.boolean(),
    estateValue: nonNegativePenceSchema,
    /**
     * Whether unused pension funds were counted IN the assessed estate, per the
     * date-driven rule in the tax layer. Lets the report state the treatment that
     * was applied rather than asserting one in static copy.
     * OPTIONAL: runs persisted before the rule existed carry no flag.
     */
    pensionsInEstate: z.boolean().optional(),
    nilRateBands: nonNegativePenceSchema,
    chargeableEstate: nonNegativePenceSchema,
    taxOnFailedGifts: nonNegativePenceSchema,
    taxOnEstate: nonNegativePenceSchema,
    totalTax: nonNegativePenceSchema,
    /**
     * A1.3 (engine 3.8.0), ADDITIVE and optional: the same assessment restated
     * as if death fell in each projection year, and per gift. Runs persisted
     * before 3.8.0 carry neither.
     */
    byYearOfDeath: z
        .array(z.object({
        year: yearSchema,
        estateValue: nonNegativePenceSchema,
        pensionsInEstate: z.boolean(),
        estimatedIht: nonNegativePenceSchema,
    }))
        .optional(),
    gifts: z.array(ihtGiftReadoutSchema).optional(),
    /**
     * A2.3, additive and optional so a run stored before this build still parses:
     * what the assessed estate is made of. Counted lines less business relief
     * less debts equal `estateValue`.
     */
    composition: z
        .object({
        lines: z
            .array(z.object({
            wrapper: wrapperSchema,
            /**
             * A2.3 review (tech-admin): SIGNED. The engine filters lines on
             * `value !== 0`, deliberately admitting a negative balance, and
             * `nonNegativePenceSchema` would have thrown inside the response
             * parse — failing the PDF export outright rather than degrading.
             */
            value: z.number().int().safe(),
            inEstate: z.boolean(),
            excludedBecause: z
                .enum([
                'pensionsOutsideEstateByRule',
                'pensionsOutsideEstateBySetting',
                'childrensJisa',
            ])
                .optional(),
        }))
            .readonly(),
        businessRelief: nonNegativePenceSchema,
        liabilities: nonNegativePenceSchema,
        /** A2.3: what the zero floor added; the table cannot disagree with the headline. */
        flooringAdjustment: z.number().int().safe(),
    })
        .optional(),
    /** A1.3, additive: what the headline assessment actually applied. */
    applied: z
        .object({
        residenceNilRateBandApplied: nonNegativePenceSchema,
        spouseExemptionApplied: z.boolean(),
        transferredAllowancesApplied: z.boolean(),
        charityRateApplied: z.boolean(),
    })
        .optional(),
    /**
     * A2.5, ADDITIVE and nullable: the estate assessed at the FIRST death, where
     * the plan models both. The headline above remains the second death — that is
     * where the tax usually falls — but a couple asking "what if he goes first"
     * were being answered by the survivor's balance sheet alone, with nothing
     * said about the estate at that point.
     *
     * `null` wherever there is no first death to assess: one person, one modelled
     * death, or a second death beyond the horizon. Absent on runs stored before
     * this build.
     *
     * The unused allowances are basis points OF THE BAND rather than an amount,
     * because what transfers at the second death is a PROPORTION (IHTA 1984 s.8A
     * for the nil-rate band, s.8G for the residence band) applied to the bands in
     * force then — not a figure frozen at the first death's rates.
     */
    /**
     * GOAL 2026-09-16 IE-2 (engine 3.19.0), ADDITIVE and optional: the full
     * working for every projection year, aligned with `byYearOfDeath`. Absent on
     * runs stored before 3.19.0 — the interface asks for a rerun rather than
     * rebuilding detail it was never given.
     */
    workingByYear: z.array(ihtYearWorkingSchema).optional(),
    firstDeath: z
        .object({
        year: yearSchema,
        /** Whose death it is, so the report can name them rather than say "the first death". */
        personId: personIdSchema,
        survivorId: personIdSchema.nullable(),
        estateValue: nonNegativePenceSchema,
        passesToSpouse: z.boolean(),
        pensionsInEstate: z.boolean(),
        nilRateBands: nonNegativePenceSchema,
        chargeableEstate: nonNegativePenceSchema,
        taxOnFailedGifts: nonNegativePenceSchema,
        taxOnEstate: nonNegativePenceSchema,
        totalTax: nonNegativePenceSchema,
        nilRateBandUnusedBps: bpsSchema.min(0).max(10_000),
        residenceNilRateBandUnusedBps: bpsSchema.min(0).max(10_000),
        /**
         * True where a gift falls on or before this death and the plan cannot say
         * whose band it consumed — the household keeps ONE gift ledger and no
         * gift carries a donor. The proportion above is then the adviser's stated
         * position, not a derived figure, and the engine says so.
         */
        transferProportionUnderived: z.boolean(),
        /** Whether the plan reaches a second death for that share to be applied at. */
        secondDeathAssessed: z.boolean().optional(),
    })
        .nullable()
        .optional(),
});
/** Stage 7 calculation trace — the machine-readable derivation per year. */
const penceIntSchema = z.number().int();
const traceBandSliceSchema = z.object({
    band: z.enum(['basic', 'higher', 'additional']),
    rateBps: bpsSchema,
    amount: nonNegativePenceSchema,
    tax: nonNegativePenceSchema,
});
const tracePotLineSchema = z.object({
    wrapper: z.nativeEnum(Wrapper),
    opening: penceIntSchema,
    contributions: nonNegativePenceSchema,
    growthAndYield: penceIntSchema,
    fees: nonNegativePenceSchema,
    withdrawn: nonNegativePenceSchema,
    otherMovements: penceIntSchema,
    closing: penceIntSchema,
});
const tracePersonSchema = z.object({
    personId: personIdSchema,
    income: z.array(z.object({
        sourceId: z.string(),
        label: z.string(),
        amount: nonNegativePenceSchema,
        taxable: z.boolean(),
    })),
    taxableIncome: z.object({
        nonSavings: nonNegativePenceSchema,
        savings: nonNegativePenceSchema,
        dividends: nonNegativePenceSchema,
    }),
    incomeTax: z.object({
        personalAllowance: nonNegativePenceSchema,
        nonSavingsTax: nonNegativePenceSchema,
        savingsTax: nonNegativePenceSchema,
        dividendTax: nonNegativePenceSchema,
        bands: z.object({
            nonSavings: z.array(traceBandSliceSchema),
            savings: z.object({
                atStartingRate: nonNegativePenceSchema,
                atPersonalSavingsAllowance: nonNegativePenceSchema,
                slices: z.array(traceBandSliceSchema),
            }),
            dividends: z.object({
                atDividendAllowance: nonNegativePenceSchema,
                slices: z.array(traceBandSliceSchema),
            }),
        }),
        marginalBand: z.enum(['none', 'basic', 'higher', 'additional']),
        section24Reduction: nonNegativePenceSchema,
        annualAllowanceCharge: nonNegativePenceSchema,
        unscheduledDrawTax: nonNegativePenceSchema,
        total: nonNegativePenceSchema,
    }),
    nationalInsurance: nonNegativePenceSchema,
    /**
     * The year's student loan repayment for this person, with the maths behind
     * it, so the drill-down can show WHY the figure is what it is instead of
     * asserting it. Its own trace line because it is not tax. OMITTED when nil.
     */
    studentLoan: z
        .object({
        plan: studentLoanPlanSchema,
        /** The threshold actually applied — held constant across the projection. */
        threshold: nonNegativePenceSchema,
        rateBps: bpsSchema,
        /** Earnings taken into account: the same base National Insurance takes. */
        earnings: nonNegativePenceSchema,
        /** The last year this loan repays is `writeOffYear - 1`. */
        writeOffYear: yearSchema,
        repayment: nonNegativePenceSchema,
    })
        .array()
        .optional(),
    capitalGains: z.object({
        standardGains: nonNegativePenceSchema,
        residentialGains: nonNegativePenceSchema,
        businessGains: nonNegativePenceSchema,
        tax: nonNegativePenceSchema,
    }),
    withdrawals: z.array(z.object({
        wrapper: z.nativeEnum(Wrapper),
        method: z.enum(['waterfall', 'scheduled', 'unscheduled']),
        gross: nonNegativePenceSchema,
        taxWithheld: nonNegativePenceSchema,
    })),
    pots: z.array(tracePotLineSchema),
});
const traceLabelledAmountSchema = z.object({
    label: z.string(),
    amount: nonNegativePenceSchema,
});
const traceDebtLineSchema = z.object({
    liabilityId: z.string(),
    label: z.string(),
    opening: nonNegativePenceSchema,
    interest: nonNegativePenceSchema,
    paid: nonNegativePenceSchema,
    closing: nonNegativePenceSchema,
    /** How a balance reached zero this year, or 'no' while it has not. */
    cleared: z.enum(['no', 'finalPayment', 'endOfTerm', 'sale']),
});
export const householdYearTraceSchema = z.object({
    year: yearSchema,
    taxYearLabel: z.string(),
    people: z.array(tracePersonSchema),
    /** Each spending item's escalated amount in this year (zero-amount items
     * omitted) — the itemised "why is spending £X" behind the year row. */
    spendingLines: z.array(traceLabelledAmountSchema),
    /** One-off life-event outflows paid this year (gifts, lump-sum costs). */
    lifeEventOutflows: z.array(traceLabelledAmountSchema),
    /** Per-liability payments this year (engine 3.4.0) — sums to the year row's
     * debtService, with the payoff year marked. OPTIONAL: runs stored under
     * earlier engines carry no debt lines and must still parse. */
    debtLines: z.array(traceDebtLineSchema).optional(),
    /** Household-level lines for assets outside the personal pots (property,
     * business, other assets, children's JISAs). */
    householdPots: z.array(tracePotLineSchema),
});
/**
 * The ONE illustrative-not-advice line, shared by every payload, report and
 * derivation so the framing can never drift between the screen, the
 * drill-down and the PDF (CLAUDE.md: every client-facing output carries it).
 *
 * "Every figure is an estimate … the actual position will differ" is load-
 * bearing, not padding. The per-asset "value is an estimate" toggle was removed
 * (W1-11) on the stated grounds that this line already said it — it did not.
 * Read by a client, "every figure follows from the stated inputs" implies the
 * inputs are verified facts and the arithmetic exact, which is the opposite of
 * the intended reading. Removing the toggle without this clause would have made
 * the report MORE confident-looking, so the two changes ship together or not
 * at all. Compliance sign-off is still required on the final wording.
 *
 * Kept deliberately close to `DISCLAIMER_TEXT` in `apps/web/src/ds/Disclaimer.tsx`,
 * which governs the in-app report; converging the two is tracked in the W1
 * review findings.
 */
export const ILLUSTRATIVE_NOT_ADVICE_DISCLAIMER = 'Illustrative modelling only, not financial advice. Every figure is an estimate that follows from the stated inputs and assumptions, with tax rules held constant, and the actual position will differ.';
/**
 * Plan-completeness warning codes. Emitted by the engine (alongside its other
 * warnings, same wire shape) when a plan's INPUTS suggest a fact-find gap —
 * e.g. nothing takes anything away, so the projection can only compound
 * upward. Data-completeness prompts, never advice. Closed here so the web
 * app and reports can key styling/grouping off a shared source of truth.
 */
export const PLAN_COMPLETENESS_WARNING_CODES = [
    'plan-no-outflows',
    'plan-no-spending',
    'plan-no-essential-spending',
    'plan-spending-ends-early',
];
export const planCompletenessWarningCodeSchema = z.enum(PLAN_COMPLETENESS_WARNING_CODES);
export const householdProjectionResultSchema = z.object({
    engineVersion: z.string().min(1),
    taxRulesVersion: z.string().min(1),
    asOfDate: isoDateSchema,
    years: z.array(householdProjectionYearSchema),
    depletionYear: yearSchema.nullable(),
    /**
     * First-class insolvency block (engine ≥ 3.1.0): set from the first year
     * the household could not meet its spending from any drawable source.
     * `illiquidValueAtFirstShortfall` names the wealth the model deliberately
     * does NOT sell (property/business/other) so "depleted" and a rising
     * net-worth line can never again appear side-by-side unexplained.
     * Optional on the wire for pre-3.1.0 stored runs.
     */
    insolvency: z
        .object({
        insolventFromYear: yearSchema,
        cumulativeUnmetShortfall: nonNegativePenceSchema,
        illiquidValueAtFirstShortfall: nonNegativePenceSchema,
    })
        .nullable()
        .optional(),
    warnings: z.array(z.object({
        code: z.string(),
        message: z.string(),
        year: yearSchema.nullable(),
        itemLabel: z.string().nullable(),
    })),
    assumptionsSnapshot: assumptionsSnapshotSchema,
    /**
     * Pre-render validation findings on the INPUTS this projection was built
     * from (engine `plan-validation`). A `blocking` finding means nothing
     * client-facing may be produced from this result — the API refuses with a
     * field-level 422 and the report renderer refuses to render.
     *
     * Optional on the wire so runs persisted before the gate existed still
     * validate; absent is NOT the same as "checked and clean", so the report
     * additionally re-derives what its narrative claims from the figures.
     */
    validation: z
        .array(z.object({
        code: z.string(),
        severity: z.enum(['blocking', 'warning']),
        path: z.string(),
        message: z.string(),
        itemLabel: z.string().nullable(),
    }))
        .optional(),
    estimatedIht: estimatedIhtSchema.nullable(),
    /**
     * C1.1 (engine 3.9.0), ADDITIVE: each market shock as resolved for this
     * run — the calendar years its steps took effect. Present only when the plan
     * carries a shock.
     */
    marketShockWindows: z
        .array(z.object({
        id: idSchema,
        label: labelSchema,
        years: z.array(yearSchema),
        replay: z.literal(true).optional(),
    }))
        .optional(),
    /** Stage 7 per-year derivation; omitted from compare runs to keep them lean. */
    trace: z.array(householdYearTraceSchema).optional(),
});
/**
 * The single-run PREVIEW response: the engine result plus the REQUIRED
 * disclaimer — the chart screen renders from this payload, so the
 * illustrative-not-advice line travels in the contract like the compare,
 * explain and persisted-run payloads (it is not just report furniture).
 */
export const householdProjectionPreviewResponseSchema = householdProjectionResultSchema.extend({
    disclaimer: z.string().min(1),
});
/**
 * Compare response: an aligned run per scenario (the base first, id "base")
 * plus the headline comparison block — depletion year, lifetime tax and
 * terminal wealth per scenario, mirroring GRAPH_REPORT_COMPARE §6.
 */
export const householdProjectionCompareResultSchema = z.object({
    /**
     * The assumption-bundle framing travels IN the contract so no UI can
     * render scenario labels without it.
     */
    disclaimer: z.string().min(1),
    comparison: z.array(z.object({
        id: idSchema,
        label: labelSchema,
        /** Net worth can be negative — plain integer pence, not non-negative. */
        terminalNetWorth: z.number().int(),
        peakNetWorth: z.number().int(),
        peakYear: yearSchema,
        depletionYear: yearSchema.nullable(),
        lifetimeTax: nonNegativePenceSchema,
    })),
    runs: z.array(z.object({ id: idSchema, label: labelSchema, result: householdProjectionResultSchema })),
});
// --- Stage 7: explain-this-year + persisted runs ------------------------------
/** Explain request: the full household request plus the year to derive. */
export const householdProjectionExplainRequestSchema = householdProjectionRequestObject
    .extend({
    explainYear: yearSchema,
})
    .superRefine(refineSingleMainResidence);
export const householdProjectionExplainResultSchema = z.object({
    year: yearSchema,
    taxYearLabel: z.string(),
    /** REQUIRED in the contract so no UI or PDF template can render the
     * derivation (or the raw trace) without the illustrative-not-advice line. */
    disclaimer: z.string().min(1),
    /** The plain-English derivation, one line per step. */
    lines: z.array(z.string()),
    /** The machine-readable trace behind the lines. */
    trace: householdYearTraceSchema,
});
/**
 * Persisted-run response: the run identity IS the audit reference — the row
 * stores the input snapshot, assumptions snapshot and all version stamps, so
 * any figure can be reproduced and attributed ("prove this number").
 */
export const householdProjectionRunReadSchema = z.object({
    runId: z.string().uuid(),
    auditRef: z.string().min(1),
    clientId: clientIdSchema,
    asOfDate: isoDateSchema,
    startYear: yearSchema,
    taxRulesVersion: z.string().min(1),
    engineVersion: z.string().min(1),
    codeVersion: z.string().min(1),
    /** ISO-8601 instant (what `isoInstantFromDb` emits), not any string. */
    createdAt: z.string().datetime(),
    /** REQUIRED: persisted-run payloads carry the illustrative-not-advice line
     * in the contract itself, like the compare response. */
    disclaimer: z.string().min(1),
    result: householdProjectionResultSchema,
});
/**
 * Run list ("which runs exist for this client" — the evidence-discovery half
 * of "prove this number"): version stamps and identity only, never the result
 * figures, so the list stays lean; fetch one run by id for the payload.
 */
export const householdProjectionRunSummarySchema = householdProjectionRunReadSchema.omit({
    result: true,
    disclaimer: true,
});
export const householdProjectionRunListSchema = z.object({
    disclaimer: z.string().min(1),
    runs: z.array(householdProjectionRunSummarySchema),
});
/**
 * GOAL 2026-09-05 D2.1 — "Since last report".
 *
 * What changed between the run behind the client's last EXPORTED report and
 * the latest run on file. Owner decision N-D2 fixed the baseline as the last
 * exported report rather than the last saved run: an adviser saves many runs
 * while working and the client has seen exactly one of them, so the saved run
 * is a comparison to something nobody was shown.
 *
 * Everything here is READ from stored rows. The prior projection is never
 * re-run: doing so would silently restate last year's figures under this
 * year's tax rules and engine, which is the one thing this panel exists to
 * make visible rather than hide.
 */
export const sinceLastReportFigureSchema = z
    .object({
    key: z.enum(['closingWealth', 'estimatedIht', 'depletionYear']),
    label: z.string().min(1),
    /**
     * Money in pence, or a calendar year for `depletionYear`; null where the
     * run has no such figure (no depletion, no IHT estimate). `unit` says
     * which, so no reader has to infer it from the key.
     */
    unit: z.enum(['pence', 'year']),
    previous: z.number().int().nullable(),
    latest: z.number().int().nullable(),
    /** latest − previous, and null when either side is absent. */
    difference: z.number().int().nullable(),
    /**
     * Whether the run ANSWERED this question. `depletionYear: null` from the
     * engine means "the plan does not run short in this projection" — the best
     * possible answer to the most-asked question in a review — and rendering it
     * the same as "this run recorded no such figure" turns it into a shrug.
     */
    previousKnown: z.boolean(),
    latestKnown: z.boolean(),
    /**
     * The projection year the figure is measured at, where it has one. A run
     * whose horizon moved compares two different ages as if they were one
     * number, and the difference absorbs it silently.
     */
    previousAtYear: yearSchema.nullable(),
    latestAtYear: yearSchema.nullable(),
})
    .strict();
/** One field that differs between the two runs' stored snapshots. */
export const sinceLastReportChangeSchema = z
    .object({
    /** Dotted path into the snapshot, e.g. `people.0.currentAge`. */
    path: z.string().min(1),
    source: z.enum(['inputs', 'assumptions']),
    /** Rendered as text: a snapshot holds numbers, strings, booleans, nulls. */
    previous: z.string().nullable(),
    latest: z.string().nullable(),
})
    .strict();
export const sinceLastReportRunSchema = z
    .object({
    runId: z.string().uuid(),
    /** The audit reference an adviser can quote back. */
    asOfDate: isoDateSchema,
    createdAt: z.string().datetime(),
    engineVersion: z.string().min(1),
    taxRulesVersion: z.string().min(1),
})
    .strict();
export const sinceLastReportSchema = z
    .object({
    clientId: idSchema,
    /**
     * Null when the client has never had a report exported. The panel says
     * "No previous report on file" and shows NOTHING else — never zeros, which
     * read as "your wealth was nil last year" rather than "there is no prior".
     */
    previous: sinceLastReportRunSchema.extend({ exportedAt: z.string().datetime() }).nullable(),
    latest: sinceLastReportRunSchema.nullable(),
    figures: z.array(sinceLastReportFigureSchema).max(3),
    changes: z.array(sinceLastReportChangeSchema).max(200),
    /**
     * True when the field-level diff was cut off at the cap. An adviser told
     * "12 things changed" when 300 did would be reading a false summary.
     */
    changesTruncated: z.boolean(),
    /** The prior run's wealth line, drawn dashed and read-only (NI-F9-01). */
    previousWealthSeries: z.array(z.object({ year: yearSchema, totalNetWorth: z.number().int() }).strict()),
    /**
     * Money here is NOMINAL — the pence the projection produced for that year,
     * not restated in today's money. Two runs a year apart are measured on
     * different bases, so a screen that prints the arithmetic has to say which
     * basis it is on, as every other money surface in this product does.
     */
    valueTerms: z.literal('nominal'),
    /**
     * REQUIRED, as on every other persisted-run payload. The panel renders the
     * line today, but a contract that merely permits it lets the next consumer
     * put stored projection figures on a screen with no disclaimer — which is
     * exactly what `householdProjectionRunReadSchema` was written to prevent.
     */
    disclaimer: z.string().min(1),
    /**
     * Set when the two runs were produced by different engine or tax-rule
     * versions. A figure that moved because HMRC's thresholds moved is not the
     * client's doing, and the panel must say so rather than let the adviser
     * present it as a change in the plan.
     */
    versionsDiffer: z.boolean(),
})
    .strict();
/**
 * GOAL 2026-09-05 D3.3 — the firm's review checklist.
 *
 * The items are the OWNER's, fixed in `docs/DECISIONS.md` under N-D3 and quoted
 * here rather than invented. The list is closed in three places — this enum,
 * migration 0038's check constraint, and the route's validation — because a
 * tick is a claim about what was DONE, and the wrong claim would turn a
 * modelling aid's file into a record of regulated advice.
 *
 * What N-D3 says the checklist NEVER contains, and what these three layers
 * therefore refuse: suitability, attitude to risk, product or fund review,
 * "recommendation agreed", "advice given", any score.
 */
export const REVIEW_CHECKLIST_ITEMS = [
    'revaluation_complete',
    'defaults_confirmed',
    'scenarios_carried_or_retired',
    'firm_tests_run',
    'goals_re_read',
    'presented',
    'pack_issued',
    'file_note_attached',
];
/**
 * Adviser-facing wording for each step. Descriptive, in the order a review
 * actually happens — the rail is a record of work, not a score, so nothing here
 * counts, ranks or grades.
 */
export const REVIEW_CHECKLIST_LABELS = {
    revaluation_complete: 'Revaluation complete',
    defaults_confirmed: 'Assumptions confirmed',
    scenarios_carried_or_retired: 'Scenarios carried forward or retired, with a reason',
    firm_tests_run: 'The firm’s usual tests run',
    goals_re_read: 'Goals re-read with the client',
    presented: 'Presented',
    pack_issued: 'Pack issued',
    file_note_attached: 'File note attached',
};
export const reviewChecklistItemSchema = z.enum(REVIEW_CHECKLIST_ITEMS);
export const reviewChecklistEntrySchema = z
    .object({
    item: reviewChecklistItemSchema,
    label: z.string().min(1),
    ticked: z.boolean(),
    /** Who put their name to the step, and when — null while it is unticked. */
    tickedBy: z.string().nullable(),
    tickedByName: z.string().nullable(),
    tickedAt: z.string().datetime().nullable(),
    note: z.string().nullable(),
})
    .strict();
export const reviewChecklistSchema = z
    .object({
    clientId: idSchema,
    /** Every item, always — an absent row is an UNTICKED step, not a missing one. */
    entries: z.array(reviewChecklistEntrySchema).length(REVIEW_CHECKLIST_ITEMS.length),
})
    .strict();
/** Tick or un-tick one step. */
export const reviewChecklistUpdateSchema = z
    .object({
    item: reviewChecklistItemSchema,
    ticked: z.boolean(),
    note: z.string().trim().min(1).max(500).nullable().optional(),
})
    .strict();
/**
 * GOAL 2026-09-05 D3.4 — the "ready to present" lock.
 *
 * A paraplanner declares a plan ready and it stops moving; an owner or adviser
 * reopens it, with a reason that is recorded. The roles are the owner's, from
 * N-D3, and the asymmetry is deliberate: preparing is a paraplanner's job,
 * reopening a plan somebody has said they will present is not.
 */
export const reviewLockSchema = z
    .object({
    clientId: idSchema,
    /**
     * How many reviews this client has COMPLETED, counting the one in progress
     * once it is declared ready. It moves only when a review completes, so an
     * ordinary fact-find edit does not invalidate an open tab — unlike
     * `factFindVersion`, which moves on every save.
     *
     * A tab carries the epoch it observed into a scenario CREATE, the one plan
     * write with no prior row to compare an `expectedUpdatedAt` against. Zero
     * for a client whose first review is still open.
     */
    reviewEpoch: z.number().int().min(0),
    /** Null when the plan is open — the ordinary state between reviews. */
    lock: z
        .object({
        lockedBy: z.string(),
        lockedByName: z.string().nullable(),
        lockedAt: z.string().datetime(),
        /** The fact-find version this lock froze. */
        factFindVersion: z.number().int().min(1),
        note: z.string().nullable(),
    })
        .strict()
        .nullable(),
})
    .strict();
/**
 * Expected version is a precondition, not evidence. The API checks it against
 * the serialized stored document and captures that document as the snapshot.
 */
export const reviewLockCreateSchema = z
    .object({
    expectedVersion: z.number().int().min(1),
    note: z.string().trim().min(1).max(500).nullable().optional(),
    /**
     * The page's clock (GOAL 2026-09-10 item 1, the reader half). When both
     * are given the API computes and STORES a projection run of the declared
     * document at that instant and binds the completed review to it, so the
     * review is rendered from that frozen calculation for ever after. Without
     * a clock the lock binds the most recent stored run, if any — legacy
     * callers and tests — and the reader says so rather than recomputing.
     */
    asOfDate: isoDateSchema.optional(),
    startYear: yearSchema.optional(),
})
    .strict()
    .refine((v) => (v.asOfDate === undefined) === (v.startYear === undefined), {
    message: 'asOfDate and startYear are given together or not at all',
    path: ['startYear'],
});
export const reviewLockReleaseSchema = z
    .object({
    expectedVersion: z.number().int().min(1),
    /**
     * REQUIRED, and the whole point. N-D3 has the unlock happen "with a logged
     * reason"; a release with no reason leaves the file saying a plan declared
     * ready was reopened by somebody, for nothing.
     */
    reason: z.string().trim().min(3).max(500),
})
    .strict();
/** Frozen evidence, not a document to replay under today's calculation rules. */
export const completedReviewSnapshotSchema = z
    .object({
    document: householdFactFindDocumentSchema,
    scenarios: z.array(z
        .object({
        id: idSchema,
        name: z.string(),
        definition: z.unknown(),
        includeInReport: z.boolean(),
        visible: z.boolean(),
        sortOrder: z.number().int(),
    })
        .strict()),
    runIds: z.array(idSchema),
    reportIds: z.array(idSchema),
    reportFiles: z.array(z.object({ id: idSchema, sha256: z.string(), exportId: idSchema }).strict()),
    /**
     * The firm's review checklist as it stood when the review was declared
     * ready (0042). Optional and additive: 0041 snapshots have none. The live
     * ticks are retired when the NEXT review starts, so this is the only place
     * "presented on the 4th, pack issued on the 6th" survives for last year's
     * review.
     */
    checklist: z
        .array(z
        .object({
        item: z.string().min(1),
        tickedAt: z.string().datetime(),
        tickedBy: z.string(),
        tickedByName: z.string().nullable(),
        note: z.string().nullable(),
    })
        .strict())
        .optional(),
})
    .strict();
/**
 * The calculation a completed review was declared ready against — identity and
 * version stamps only. The figures come from `completedReviewReadSchema`, which
 * carries the STORED result; nothing here is recomputed.
 */
export const frozenRunRefSchema = z
    .object({
    runId: z.string().uuid(),
    auditRef: z.string().min(1),
    asOfDate: isoDateSchema,
    startYear: yearSchema,
    createdAt: z.string().datetime(),
    taxRulesVersion: z.string().min(1),
    engineVersion: z.string().min(1),
    codeVersion: z.string().min(1),
})
    .strict();
/** One headline figure read off a STORED result (never recomputed). */
export const storedFigureSchema = z
    .object({
    key: z.enum(['closingWealth', 'estimatedIht', 'depletionYear']),
    label: z.string().min(1),
    unit: z.enum(['pence', 'year']),
    value: z.number().int().nullable(),
    /** Whether the stored run ANSWERED the question (see `sinceLastReportFigureSchema`). */
    known: z.boolean(),
    atYear: yearSchema.nullable(),
})
    .strict();
const completedReviewItemSchema = z
    .object({
    id: idSchema,
    completedAt: z.string().datetime(),
    completedBy: z.string(),
    completedByName: z.string().nullable(),
    note: z.string().nullable(),
    nextReviewStartedAt: z.string().datetime().nullable(),
    nextReviewStartedByName: z.string().nullable(),
    nextReviewReason: z.string().nullable(),
    factFindVersion: z.number().int().min(1),
    /**
     * Null for a legacy lock (before 0042) or one set without a clock: the
     * reader then shows NO figures for the review — never a recomputation
     * wearing the review's date.
     */
    frozenRun: frozenRunRefSchema.nullable(),
    /** Read from the frozen run's stored result; empty when there is none. */
    figures: z.array(storedFigureSchema).max(3),
    /** Reports issued as at completion — ids only, files via the export routes. */
    issuedReports: z.array(z.object({ exportId: idSchema, fileId: idSchema, sha256: z.string() }).strict()),
    snapshot: completedReviewSnapshotSchema.nullable(),
})
    .strict();
export const reviewHistorySchema = z
    .object({
    clientId: idSchema,
    reviews: z.array(completedReviewItemSchema),
})
    .strict();
/**
 * ONE completed review, rendered from its frozen calculation (GOAL 2026-09-10
 * item 1 — the reader). `frozenRun.result` is the stored `household_projection_runs`
 * row exactly as persisted; every figure and series a screen draws for this
 * review comes from it. `changesFromPrevious` is the id-keyed diff of the
 * previous completed review's inputs against this one's — the "what changed"
 * an adviser reads when comparing two reviews — with both runs' version stamps,
 * so a movement HMRC caused is never attributed to the client.
 */
export const completedReviewReadSchema = z
    .object({
    clientId: idSchema,
    review: completedReviewItemSchema,
    frozenRun: householdProjectionRunReadSchema.nullable(),
    /** The stored wealth line (headline net worth per year), for a chart drawn from evidence. */
    wealthSeries: z.array(z.object({ year: yearSchema, totalNetWorth: z.number().int() }).strict()),
    previousReview: z
        .object({
        id: idSchema,
        completedAt: z.string().datetime(),
        frozenRun: frozenRunRefSchema.nullable(),
        figures: z.array(storedFigureSchema).max(3),
    })
        .strict()
        .nullable(),
    changesFromPrevious: z.array(sinceLastReportChangeSchema).max(200),
    changesTruncated: z.boolean(),
    versionsDiffer: z.boolean(),
    valueTerms: z.literal('nominal'),
    disclaimer: z.string().min(1),
})
    .strict();
/**
 * What the adviser opens the NEXT review on (GOAL 2026-09-10 item 2). The
 * live document is compared with the last COMPLETED review's snapshot — facts
 * carried forward are the same rows, so nothing is duplicated and only what
 * moved is listed — and the items that ordinarily need a decision at an
 * annual review are pointed at: income that has ended, events that have
 * passed, valuations not updated since the last review, household changes,
 * and a tax-rules version that differs from the one the last review was run
 * under. Every entry is a FACT about the file; none is a verdict on the plan.
 */
export const REVIEW_ATTENTION_KINDS = [
    'income_ended',
    'event_passed',
    'valuation_unchanged',
    'household_changed',
    'tax_rules_changed',
    'engine_changed',
];
export const reviewAttentionSchema = z
    .object({
    kind: z.enum(REVIEW_ATTENTION_KINDS),
    /** Plain statement of the fact, e.g. "Salary (Alex) ended in 2025". */
    label: z.string().min(1),
    /** The document item it refers to, when there is one. */
    itemId: z.string().nullable(),
    /** `people`, `incomeStreams`, `assets`, `lifeEvents`, … or null. */
    section: z.string().nullable(),
})
    .strict();
export const reviewStartSchema = z
    .object({
    clientId: idSchema,
    asOfDate: isoDateSchema,
    startYear: yearSchema,
    /** Null when the client has no completed review: a first review has nothing to carry. */
    previousReview: z
        .object({
        id: idSchema,
        completedAt: z.string().datetime(),
        completedByName: z.string().nullable(),
        factFindVersion: z.number().int().min(1),
        frozenRun: frozenRunRefSchema.nullable(),
    })
        .strict()
        .nullable(),
    /** When the current draft was started (the previous review's release), if any. */
    draftStartedAt: z.string().datetime().nullable(),
    /** The rule set the NEXT calculation would use for `startYear`; null if none exists. */
    currentTaxRulesVersion: z.string().nullable(),
    changes: z.array(sinceLastReportChangeSchema).max(200),
    changesTruncated: z.boolean(),
    attention: z.array(reviewAttentionSchema).max(200),
    disclaimer: z.string().min(1),
})
    .strict();
//# sourceMappingURL=schemas-household.js.map